// The Pixi board (slices 06, 07 and 08; plan §3): the map at its natural
// size, or the empty board; the fitted grid derived from the table's
// anchors; the things the projection gave this person, drawn at their
// footprints; pan and zoom with ordinary mouse behaviour; each person's own
// view. In grid setup the DM drags lines and clicks padlocks here. A token
// is dragged through the squares it takes — the hand's path IS the route —
// and the move commits on release as one event, after which everyone,
// the mover included, watches it travel (design/12 §4). Driven outside
// React's render loop: React mounts the application and hands it what
// changed; the scene redraws itself.
import { useEffect, useRef, useState } from 'react';
// The deployed origin's CSP is `script-src 'self'` with no 'unsafe-eval'
// (e2e/csp.spec.js's pattern; the header is nginx's on cosmo). Pixi builds
// shader glue with `new Function` unless this module is loaded first; without
// it the application never initialises and the board is a blank host — which
// the local Vite server, having no CSP, never shows (found on vtt-dev, 2026-09-25).
import 'pixi.js/unsafe-eval';
import { Application, CanvasSource, CanvasTextMetrics, ColorMatrixFilter, Container, Graphics, Sprite, Text, Texture, VideoSource, type FederatedPointerEvent } from 'pixi.js';
import {
  BOARD_BG, isGridless, isTiledMap, lineSegments, rawDistanceLabel, blankArea, GRID_LIMITS,
  OBJECT_LIMITS, PING_MS, boardExtent, deriveAxis, dragLine, footprintAt, fromBoard, lockLine, snapToCells, stepCell, toBoard, unlockLine,
  WALL_IN_THE_WAY, blocksMove, blocksSight, routeBlocked, type Wall, type PlacedLight, tileBox, tileLayout,
  DEFAULT_OVERLAYS, areaSpans, placeStamp, cellOf, cellsInBox, rulerLabel, type DistanceRules, type Area, type Drawing, dmEncounterView, previewRefusal, type BoardView, type Cell, type DmObject, type Encounter, type EventBody, type Focus, type Grid, type MapInfo, type Overlays, type PlayerEncounter, type PlayerObject, type Point,
  type Zone, woundsOf, type WoundResource,
  NO_SPEED_NOTE, routeCost, stepTakenNote,
  type Span, wallsOnFloor, groundFloor, floorOf, neighbourFloors, joinsAt, type Floor, elevation, distanceLabel, squaresBetween3D,
} from '@dragons-table/vtt-shared';
import { cellRects } from './focusArrows';
import { focusTool } from './focusTool';
import { fetchView, pictureBlob, pictureUrl, releaseTiles, releaseTilesExcept, saveView, tileUrl } from '../assets';
import { AnimationLoop, animateSetting, animationStats, makeMoving, type Moving, type MovingEnv } from './animated';
import { useShell } from '../shell/context';
import { useTableState, type Travel } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { healthOf, useHealthResource, useNumberWords, useWoundRules, type HealthResource, type NumberWords } from '../table/numberWords';
import { boardBus } from './bus';
import { boardGold, conditionBadges, getLook, sideColour, sideOutline, sideOutlines, type SideOutlines, initialsOf, shownConditions, identityColour, markerColour, plainDiscColour, setLook, wearsHpPips } from './tokenStatus';
import { CONCENTRATING_BADGE } from './tokenStatus';
import { boardDropProps } from '../place';
import { gridEditor } from './gridEditor';
import { capRoute, extendRoute, finishRoute, pointAlong, travelMs } from './route';
import { clampView, fitToScreen, pan, sameView, wheelFactor, zoomAt, type Extent } from './scene';
import { selection } from './selection';
import { floorView, onFloor, viewedFloor } from './floorView';
import { limitHitToFootprint } from './tokenHit';
import { stackWorld } from './worldStack';
import { fitName, plateLimits, type Plate } from './plates';
import { fanOut, raiseOrder, type FanOffset } from './fanOut';
import { areaTool } from './areaTool';
import { targetTool } from './targetTool';
import { whatsThisTool } from './whatsThisTool';
import { measureTool } from './measureTool';
import { boardSquares, cellCentre, nearestIntersection, spanRects } from './areaGeometry';
import { drawEffects, effectsBus, startPlaying, type Playing } from './effects';
import { auraPaints } from './auraGeometry';
import { useBoardMap, useMapTiles, useMapVersion } from './sceneMap';
import { PyramidLayer } from './pyramidLayer';
import { fogTool } from './fogTool';
import { ZONE_TINTS, zoneTool } from './zoneTool';
import { difficultTest, dragCostLabel, dragReadout, heldDifficult } from './dragReadout';
import { areaColours, confirmedMove, drawArea, moveStore, pendingLabel, tokenArea, type AreaState } from './moveArea';
import { fogPaint, fogStyle, sightPaint, type FogStyle } from './fogGeometry';
import { fogOnFloor } from './floorFog';
import { followerAt, groupMoves, groupOf, moveRev, steppable, type Follower, type GroupThing } from './group';
import { keyStep, type StepThing } from './keyStep';
import { LONG_PRESS_MS, TouchGestures, callOffTapMoveOnModes, onBoard, tapMove, tapMoveStore, tokenAtTouch, type TouchIntent } from './touch';
import { drawTool } from './drawTool';
import { bindBoardCursor } from './boardCursor';
import { cancelPress } from './pressCancel';
import { typingIn } from './typing';
import { toggleRing } from './ToggleLights';
import { LateScene } from './lateScene';
import { DOOR_PICK_PX, WALL_PICK_PX, doorAt, doorClick, doorMarks, wallAt, wallCorner, wallTool, type DoorMark } from './wallTool';

/** How near (screen pixels) a click must be to a placed light to pick it (issue 1145). */
const LIGHT_PICK_PX = 10;
const LIGHT_COLOUR = 0xfb923c;
import { clampToBoard, erasableAt, shapeBox, simplify, type Pt } from './drawGeometry';

const SAVE_DELAY_MS = 600;
/** The longest a still board goes without a frame drawn: the safety net under rendering on demand. */
const IDLE_REDRAW_MS = 1000;
const HANDLE_R = 9;
type Thing = PlayerObject | DmObject;

interface Scene {
  app: Application;
  world: Container;
  map: Sprite;
  floor: Graphics;
  /** No map picture: the board is the plain floor (issue 1409: what a player's remembered squares are washed over). */
  emptyFloor?: boolean;
  grid: Graphics;
  handles: Container;
  things: Container;
  overlay: Graphics;
  view: BoardView;
  extent: Extent;
  destroyed: boolean;
  /** What the grid layer last drew from, so the draft and the store can both drive it. */
  gridSource: Grid | null;
  /** The grid the things sit on: the store's, never a draft. */
  objectsGrid: Grid;
  editing: boolean;
  isDm: boolean;
  /** A co-DM holding `tokens` (issue 1159): they move any token as the DM does, on whichever board they see. */
  movesAny?: boolean;
  say: (text: string) => void;
  dragging: { axis: 'x' | 'y'; index: number } | null;
  /** The token under the hand, its route so far, and its size for snapping. */
  tokenDrag: { id: string; route: Point[]; size: { w: number; h: number }; followers: Follower[]; lockedStayed: number; rev: number } | null;
  /** Where each follower of a group drag stands right now (issue 830), so a redraw mid-drag keeps it there. */
  groupAt: Map<string, Point>;
  /** A thing took this press (selected it, locked or not), so the host must not clear the selection or start a box. */
  pressed: boolean;
  /** A press on a thing this person may not move (issue 795): said once the pointer travels, never on the click. */
  notMine: { x: number; y: number; name: string | undefined; said: boolean } | null;
  box: { from: Point; to: Point } | null;
  /** An area left ungridded being drawn in grid setup (issue 1035). */
  blankBox?: { from: Point; to: Point } | null;
  nodes: Map<string, ThingNode>;
  objects: Record<string, Thing>;
  snap: boolean;
  /** Which overlays this table draws (design/19 §5): from the scene, never per token. */
  overlays: Overlays;
  /** The words the overlays' hover labels use (issue 1309): the system's, else the generic ones. Read at hover. */
  words: NumberWords;
  /** The resource the health bar shows on a system that names one (issue 1312); null, hit points. */
  health: HealthResource | null;
  /** The system's resources as the wounds read them (issue 1526); empty, the system's resources have no levels. */
  wounds: WoundResource[];
  /** Moves playing out: where a node is along its route, by id. */
  travels: Map<string, { route: Point[]; started: number; ms: number }>;
  /** The turn order as this viewer reads it (slice 12): the lock's preview and the turn marker draw from it. */
  encounter: PlayerEncounter | null;
  /** The tokens acting now, for the one neutral marker (design/20 §5) — only ones this viewer can see are ever in the store. */
  turnTokens: Set<string>;
  /** Tokens with a Toggle on (qa-runs#371; ToggleLights.tsx `toggleRing`): each one's ring colour, drawn round it. */
  toggleRings?: Map<string, number>;
  /** Fan-out (issue 1030): each stacked thing's display offset, from what this viewer was given. Positions never change. */
  fan: Map<string, FanOffset>;
  /** This viewer's draw order within a layer (issue 1030): a click counter per id, the last clicked on top. Never sent. */
  raised: Map<string, number>;
  raiseCount: number;
  /** Focus highlights (slice 13): everyone's, each in its owner's identity colour; the layer sits between the grid and the things. */
  focusLayer: Graphics;
  /** Quick pings (issue 1039): each where it was made, in its maker's colour, fading over PING_MS. Never stored. */
  pingLayer: Graphics;
  pings: Array<{ x: number; y: number; colour: number; started: number }>;
  /** Visual effects (issue 1164): drawn per frame only while one plays, then the layer is cleared. Never stored. */
  fxLayer: Graphics;
  effects: Playing[];
  focuses: Record<string, Focus>;
  focusColours: Record<string, number>;
  /** Pointing mode: a drag draws a box of squares, a click toggles one (design/08 §5). */
  pointing: boolean;
  focusDrag: { from: Cell; to: Cell; moved: boolean } | null;
  hovered: string | null;
  /** The scene's floors as this screen holds them (qa-runs#1160): the ruler reads tokens' heights by them. */
  floors?: Floor[] | undefined;
  /** The floor in view for the fog (qa-runs#1160): a player's explored squares are drawn for that floor alone. */
  fogFloor?: string | null;
  /** The DM is placing the table (issue 811): a move lands on its square with no route, for nobody to watch. */
  placing: boolean;
  /** Area templates (issue 832): the table's, each in its owner's colour, under the focuses; the draft being laid on top. */
  areaLayer: Graphics;
  /** Auras around tokens (issue 1150): under the tokens, redrawn when a token in the store moves or its auras change. */
  auraLayer: Graphics;
  /** The mover's reachable area (qa-runs#1161): over the auras, under the things; only this screen's own. */
  moveLayer: Graphics;
  areas: Area[];
  areaMode: boolean;
  /** The action bar's targeting step (issue 1130): the next tap on a token is the attack's target — no selection, no drag. */
  targeting: boolean;
  /** "What's this?" (issue 1155): a tap on a token opens its wiki card — no selection, no drag. */
  explaining: boolean;
  /** The table's system's distance rule (issue 1303): the ruler's unit and diagonals, the draft area's diagonal. None is squares. */
  distance: DistanceRules | null;
  /** The squares of difficult terrain this screen holds (issue 1151), as a cell test for the ruler; none, undefined. */
  difficult?: (c: Cell) => boolean;
  /** Where the pointer is while an area is drafted and not yet placed: the preview follows it. */
  areaHover: Cell | null;
  /** The ruler (issue 832): this person's alone. */
  measureLayer: Graphics;
  measureText: Text;
  measuring: boolean;
  measureDrag: boolean;
  /** Drawings on the map (issue 1032): above the things and the fog, one cached node a drawing, rebuilt only when it changes. */
  drawLayer: Container;
  drawNodes: Map<string, { d: Drawing; node: Container }>;
  /** The mark being drawn (only on the drawer's screen) and the Erase highlight, over the drawings. */
  drawDraft: Graphics;
  drawings: Drawing[];
  drawing: boolean;
  drawPress: { points: number[]; pointerId: number } | null;
  /** The drawing Erase would take under the pointer: only one's own (or, for the DM, an ownerless one). */
  drawHover: string | null;
  /** Touch pointers down while drawing: two pan and zoom (design/28 §4.6). */
  touches: Map<number, { x: number; y: number }>;
  /** The token a finger's press landed on (issue 1178), set by its node's handler for the host's touch handling. */
  touchToken?: string | null;
  /** This person's own identity colour, raw: the first swatch, and the colour of a mark drawn before choosing one. */
  ownHex: string;
  /** Whose side outlines this screen draws (qa-runs#1157): every token's, this player's own, or none (turned off). */
  sides?: SideOutlines;
  /** Fog of war (issue 851): over the map and the things, the DM's shade or view-as's black; nothing for a player. */
  fogLayer: Graphics;
  fog: { on: boolean; revealed: Array<{ j: number; i0: number; i1: number }> } | null;
  fogStyle: FogStyle;
  /** The DM's reveal or cover mode: a drag is a box of squares, a click one. */
  fogging: boolean;
  /** Vision zones (issue 1149): the DM's, drawn on the fog layer a tint a kind; and the DM's painting mode. A player's: none. */
  zones: Zone[];
  zoning: boolean;
  /**
   * Walls (issue 1144): the DM's alone, on their own layer above the map and below the things. A player's layer holds
   * only the door markers (issue 1148) of the doors their store holds.
   */
  wallLayer: Graphics;
  /** A player's map in tiles (issue 1144, slice 3): black under all of it, one sprite a tile they may see something of, at the hash it shows. */
  tileLayer: Container;
  tileBack: Graphics;
  tileSprites: Map<string, { hash: string; sprite: Sprite }>;
  /** The map whose tiles the board holds (issue 1144): a switch lets every tile of the one before go. */
  tilesOf: string | null;
  /** A big map (issue 1172): drawn from its pyramid, only the tiles in view at the zoom's level; the whole picture never fetched. */
  pyramid: PyramidLayer;
  walls: Wall[];
  /** qa-runs#1160 slice B: the openings of the floor in view and of the one above it (where it opens onto this one), as rows of squares. */
  openings?: Array<{ j: number; i0: number; i1: number }>;
  /**
   * Doors (issue 1148; design/30 §1.2): a marker at each door's midpoint, clicked outside any tool. The DM's every door;
   * a player's those their store holds, as their group last saw them (§4).
   */
  doors: DoorMark[];
  /** The DM's placed lights (issue 1145), drawn on the wall layer while the wall tool is open; a player's: none. */
  lights: PlacedLight[];
  /** The DM's wall mode: each click a corner of the chain, or a wall picked. */
  walling: boolean;
  /**
   * Something on the board changed since the last frame was drawn. The board renders on demand: Pixi's own ticker
   * re-drew the whole stage every frame, nothing changing, which kept a still board's main thread busy all the time.
   */
  dirty: boolean;
}

/** Every focus, and the draft being pointed at, over the grid. */

/** A nameplate's height on screen, in pixels, whatever the zoom (issue 787). */
const NAMEPLATE_PX = 13;
/** How far the hand travels with the button held before a press on something not yours reads as a drag. */
const NOT_MINE_SLOP_PX = 4;
/** The one refusal a person could not tell had happened: said in words, like "… is locked." */
export const notYours = (name: string | undefined): string => `${name ?? 'That token'} is not yours to move.`;

function drawFocuses(scene: Scene, own: number): void {
  scene.dirty = true;
  const g = scene.focusLayer;
  g.clear();
  const z = scene.view.zoom;
  const paint = (cells: Cell[], colour: number, alpha: number, strong: boolean) => {
    for (const r of cellRects(cells, scene.objectsGrid, scene.extent)) {
      g.rect(r.x, r.y, r.width, r.height).fill({ color: colour, alpha });
      g.rect(r.x + 1 / z, r.y + 1 / z, r.width - 2 / z, r.height - 2 / z).stroke({ color: colour, width: (strong ? 3 : 2) / z, alpha: 0.95 });
    }
  };
  for (const f of Object.values(scene.focuses)) paint(f.cells, scene.focusColours[f.ownerId] ?? 0xa8a29e, scene.hovered === f.id ? 0.45 : 0.3, scene.hovered === f.id);
  if (scene.pointing) {
    paint(focusTool.get().cells, own, 0.4, true);
    if (scene.focusDrag) paint(cellsInBox(scene.focusDrag.from, scene.focusDrag.to), own, 0.2, false);
  }
}

/** Every area on the table, and the draft being laid, shaded over the fitted grid (issue 832). */
function drawAreas(scene: Scene, own: number): void {
  scene.dirty = true;
  const g = scene.areaLayer;
  g.clear();
  const z = scene.view.zoom;
  const { cols, rows } = boardSquares(scene.objectsGrid, scene.extent);
  const paint = (a: Area, colour: number, alpha: number) => {
    for (const r of spanRects(areaSpans(a, cols, rows), scene.objectsGrid, scene.extent)) {
      g.rect(r.x, r.y, r.width, r.height).fill({ color: colour, alpha }).stroke({ color: colour, width: 1.5 / z, alpha: 0.9 });
    }
  };
  for (const a of scene.areas) paint(a, scene.focusColours[a.ownerId] ?? 0xa8a29e, 0.22);
  const d = areaTool.get();
  const origin = d.origin ?? scene.areaHover;
  // The draft is drawn by the system's diagonal rule, as the server will stamp it; with none, no area can be laid.
  if (scene.areaMode && origin && scene.distance) paint({ id: 'draft', ownerId: 'draft', shape: d.shape, origin, size: d.size, size2: d.shape === 'rect' ? d.size2 : d.size, diagonal: scene.distance.diagonal, includes: null, dir: d.dir, label: '' }, own, d.origin ? 0.4 : 0.2);
}

/** Every aura on a token in this viewer's store (issue 1150), a translucent shape under the tokens, outlined. */
function drawAuras(scene: Scene, objects: Iterable<{ x: number; y: number; size: { w: number; h: number }; auras?: Array<{ id: string; radius: number; colour?: string }> }>): void {
  scene.dirty = true;
  const g = scene.auraLayer;
  g.clear();
  const z = scene.view.zoom;
  for (const p of auraPaints(objects, scene.objectsGrid, scene.extent, scene.distance?.diagonal ?? null)) {
    for (const r of p.rects) g.rect(r.x, r.y, r.width, r.height).fill({ color: p.colour, alpha: 0.14 });
    if (p.circle) g.circle(p.circle.x, p.circle.y, p.circle.r).fill({ color: p.colour, alpha: 0.14 }).stroke({ color: p.colour, width: 1.5 / z, alpha: 0.7 });
  }
}

/** The covered squares (issue 851), one rectangle a row span, and the DM's box being dragged. */
function drawFog(scene: Scene): void {
  scene.dirty = true;
  const g = scene.fogLayer;
  g.clear();
  const paint = fogPaint(scene.fog, scene.fogStyle, scene.objectsGrid, scene.extent, boardSquares(scene.objectsGrid, scene.extent));
  // With the scene's vision on (issue 1144), a player's fog carries their explored and seen squares: never explored is
  // black, explored but out of sight dimmed. Without it, a player's covered squares are painted black under her tokens,
  // over the masked map, and over the plain floor where there is no map to mask (qa-runs 403).
  // On the dark theme's plain floor (no map picture) half-black is the colour of the space round the board, so the
  // squares a player remembers read as black (issue 1409): there they are washed grey instead.
  const memory = scene.emptyFloor !== false && getLook().theme === 'dark';
  // Floors (qa-runs#1160): the explored squares of the floor in view only (the ground floor's are `explored`, the others' `exploredOn`).
  type PaintFog = Parameters<typeof sightPaint>[0] & { exploredOn?: Record<string, Span[]> };
  const floorFog = scene.fog && scene.floors && scene.floors.length > 1 ? fogOnFloor(scene.fog as PaintFog, scene.fogFloor ?? null, groundFloor(scene.floors)) : scene.fog as PaintFog;
  const sight = scene.fogStyle !== 'shade' ? sightPaint(floorFog, scene.objectsGrid, scene.extent, boardSquares(scene.objectsGrid, scene.extent), { memory }) : null;
  if (sight) { for (const layer of sight) for (const r of layer.rects) g.rect(r.x, r.y, r.width, r.height).fill({ color: layer.color ?? 0x000000, alpha: layer.alpha }); }
  else if (paint) for (const r of paint.rects) g.rect(r.x, r.y, r.width, r.height).fill({ color: 0x000000, alpha: paint.alpha });
  // Vision zones (issue 1149): the DM's, a tint a kind, and the box being painted or erased.
  for (const zone of scene.zones) for (const r of spanRects(zone.spans, scene.objectsGrid, scene.extent)) g.rect(r.x, r.y, r.width, r.height).fill({ color: ZONE_TINTS[zone.kind] ?? 0x94a3b8, alpha: 0.3 });
  // Openings between floors (qa-runs#1160 slice B): a stair, a pit, a balcony's edge — a sky-blue tint for everyone who holds the floor.
  for (const r of spanRects(scene.openings ?? [], scene.objectsGrid, scene.extent)) g.rect(r.x, r.y, r.width, r.height).fill({ color: 0x38bdf8, alpha: 0.28 });
  // Difficult terrain a player was sent (issue 1151): the squares of it in her explored map, tinted as the DM's are.
  const held = (scene.fog as { difficult?: Array<{ j: number; i0: number; i1: number }> } | null)?.difficult;
  if (!scene.zones.length && held) for (const r of spanRects(held, scene.objectsGrid, scene.extent)) g.rect(r.x, r.y, r.width, r.height).fill({ color: ZONE_TINTS.difficult, alpha: 0.3 });
  const zd = zoneTool.get();
  if (scene.zoning && zd.box) {
    const zr = { i0: Math.min(zd.box.from.i, zd.box.to.i), j0: Math.min(zd.box.from.j, zd.box.to.j), i1: Math.max(zd.box.from.i, zd.box.to.i), j1: Math.max(zd.box.from.j, zd.box.to.j) };
    const rows = [];
    for (let j = zr.j0; j <= zr.j1; j += 1) rows.push({ j, i0: zr.i0, i1: zr.i1 });
    for (const r of spanRects(rows, scene.objectsGrid, scene.extent)) g.rect(r.x, r.y, r.width, r.height).fill({ color: zd.mode === 'erase' ? 0xfbbf24 : ZONE_TINTS[zd.mode], alpha: 0.45 });
  }
  const d = fogTool.get();
  if (!scene.fogging || !d.box) return;
  const z = scene.view.zoom;
  const from = { i: Math.min(d.box.from.i, d.box.to.i), j: Math.min(d.box.from.j, d.box.to.j) };
  const to = { i: Math.max(d.box.from.i, d.box.to.i), j: Math.max(d.box.from.j, d.box.to.j) };
  const spans = [];
  for (let j = from.j; j <= to.j; j += 1) spans.push({ j, i0: from.i, i1: to.i });
  const colour = d.mode === 'reveal' ? 0xfbbf24 : 0x0c0a09;
  for (const r of spanRects(spans, scene.objectsGrid, scene.extent)) g.rect(r.x, r.y, r.width, r.height).fill({ color: colour, alpha: d.mode === 'reveal' ? 0.25 : 0.45 });
  const [a] = spanRects([{ j: from.j, i0: from.i, i1: to.i }], scene.objectsGrid, scene.extent);
  const [b] = spanRects([{ j: to.j, i0: from.i, i1: to.i }], scene.objectsGrid, scene.extent);
  if (a && b) g.rect(a.x, a.y, a.width, b.y + b.height - a.y).stroke({ color: 0xfbbf24, width: 2 / z, alpha: 0.95 });
}

/** A dashed circle: `dash` drawn, `gap` left, in board pixels along its edge. */
function dashedCircle(g: Graphics, x: number, y: number, r: number, dash: number, gap: number): void {
  if (r <= 0) return;
  const step = (dash + gap) / r; const on = dash / r;
  for (let t = 0; t < Math.PI * 2; t += step) g.moveTo(x + r * Math.cos(t), y + r * Math.sin(t)).arc(x, y, r, t, Math.min(Math.PI * 2, t + on));
}

/** A dashed line from a to b: `dash` drawn, `gap` left, in board pixels. */
function dashed(g: Graphics, a: Point, b: Point, dash: number, gap: number): void {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (!len) return;
  const ux = (b.x - a.x) / len; const uy = (b.y - a.y) / len;
  for (let t = 0; t < len; t += dash + gap) {
    const e = Math.min(len, t + dash);
    g.moveTo(a.x + ux * t, a.y + uy * t).lineTo(a.x + ux * e, a.y + uy * e);
  }
}

const WALL_COLOUR = 0xfacc15; const DOOR_COLOUR = 0x38bdf8; const WALL_PICKED = 0xffffff;

/**
 * The DM's walls (issue 1144): a thin bright line a wall; a door another colour, solid closed and dashed open; a wall
 * that blocks only movement or only sight dashed. The picked wall is drawn white, and the chain being drawn on top,
 * with its rubber band to the hand. Sized in screen pixels, so redrawn on a zoom.
 */
function drawWalls(scene: Scene): void {
  scene.dirty = true;
  const g = scene.wallLayer;
  g.clear();
  const z = scene.view.zoom;
  const w = 2 / z; const dash = 8 / z; const gap = 5 / z;
  const picked = wallTool.get().selected;
  for (const wall of scene.isDm ? scene.walls : []) {
    const colour = wall.id === picked ? WALL_PICKED : wall.door ? DOOR_COLOUR : WALL_COLOUR;
    const whole = wall.door ? !wall.door.open : blocksMove(wall) && blocksSight(wall);
    if (whole) g.moveTo(wall.a.x, wall.a.y).lineTo(wall.b.x, wall.b.y);
    else dashed(g, wall.a, wall.b, dash, gap);
    g.stroke({ color: colour, width: wall.id === picked ? w * 2 : w, alpha: 0.95 });
  }
  // Each door's marker (issue 1148), for the DM and a player alike: a small square, filled closed and hollow open.
  const half = 6 / z;
  for (const d of scene.doors) {
    g.rect(d.x - half, d.y - half, half * 2, half * 2);
    if (d.open) g.fill({ color: 0x0c0a09, alpha: 0.6 }); else g.fill({ color: DOOR_COLOUR, alpha: 1 });
    g.rect(d.x - half, d.y - half, half * 2, half * 2).stroke({ color: d.open ? DOOR_COLOUR : 0x0c0a09, width: 1.5 / z, alpha: 1 });
  }
  if (!scene.isDm || !scene.walling) return;
  const d = wallTool.get();
  // Placed lights (issue 1145), while the tool is open: a dot at each, a ring at its bright and its dim radius.
  const pitch = scene.objectsGrid.x.basePitch;
  for (const l of scene.lights) {
    const picked = l.id === d.light;
    g.circle(l.x, l.y, (picked ? 7 : 5) / z).fill({ color: picked ? WALL_PICKED : LIGHT_COLOUR, alpha: l.on ? 1 : 0.4 });
    if (d.lights) {
      if (l.bright > 0) g.circle(l.x, l.y, l.bright * pitch).stroke({ color: LIGHT_COLOUR, width: 1 / z, alpha: l.on ? 0.6 : 0.25 });
      dashedCircle(g, l.x, l.y, l.dim * pitch, dash, gap);
      g.stroke({ color: LIGHT_COLOUR, width: 1 / z, alpha: l.on ? 0.5 : 0.2 });
    }
  }
  if (d.lights) return;
  const pts = d.hover && d.chain.length ? [...d.chain, d.hover] : d.chain;
  if (pts.length > 1) {
    g.moveTo(pts[0]!.x, pts[0]!.y);
    for (const p of pts.slice(1)) g.lineTo(p.x, p.y);
    g.stroke({ color: WALL_COLOUR, width: w, alpha: 0.7 });
  }
  for (const p of d.chain) g.circle(p.x, p.y, 3 / z).fill({ color: WALL_COLOUR });
  if (d.hover) g.circle(d.hover.x, d.hover.y, 3 / z).stroke({ color: WALL_COLOUR, width: 1 / z });
}

/**
 * Whether the screen refuses a move before sending it (issue 1144): a route that meets a wall that blocks movement,
 * by the server's own rule (`routeBlocked`). Only the walls this screen holds — the DM's; a player holds none, and is
 * told by the server's refusal. The DM passes walls while placing the table, as the server allows.
 */
function wallRefuses(scene: Scene, moves: ReadonlyArray<{ id: string; route: Point[] }>): boolean {
  if (!scene.walls.length || (scene.isDm && scene.placing)) return false;
  return moves.some((m) => { const o = scene.objects[m.id]; return o ? routeBlocked({ x: o.x, y: o.y }, m.route, scene.walls) : false; });
}

/** This person's ruler (issue 832): a line from square to square and the distance by the table's rule. */
function drawRuler(scene: Scene): void {
  scene.dirty = true;
  const g = scene.measureLayer; const t = scene.measureText;
  g.clear();
  const r = measureTool.get();
  if (!r.on || !r.from || !r.to) { t.visible = false; return; }
  const z = scene.view.zoom;
  // No grid (issue 1035): from point to point, the straight distance.
  const gridless = isGridless(scene.objectsGrid) && r.fromAt && r.toAt;
  const a = gridless ? r.fromAt! : cellCentre(scene.objectsGrid, scene.extent, r.from);
  const b = gridless ? r.toAt! : cellCentre(scene.objectsGrid, scene.extent, r.to);
  // Dashed gold, its ends dotted (issue 1541), and the distance on a chrome pill with a gold edge at the line's middle.
  const gold = boardGold();
  dashed(g, a, b, 7 / z, 5 / z);
  g.stroke({ color: gold, width: 2.5 / z, alpha: 0.95 });
  g.circle(a.x, a.y, 4 / z).fill({ color: gold }); g.circle(b.x, b.y, 4 / z).fill({ color: gold });
  // Height (qa-runs#1160; design/32 §3): from a token to a token, the ruler counts the height between them.
  const at = (c: Cell) => Object.values(scene.objects).find((o) => o.kind === 'token' && cellOf(scene.objectsGrid, scene.extent, { x: o.x, y: o.y }).i === c.i && cellOf(scene.objectsGrid, scene.extent, { x: o.x, y: o.y }).j === c.j) as (Thing & { floor?: string; z?: number }) | undefined;
  const ta = !gridless && scene.distance ? at(r.from) : undefined; const tb = ta ? at(r.to) : undefined;
  const sceneFl = { floors: scene.floors };
  const climb = ta && tb ? elevation(tb, sceneFl) - elevation(ta, sceneFl) : 0;
  t.text = gridless ? rawDistanceLabel(a, b, scene.objectsGrid, scene.distance)
    : climb && scene.distance ? distanceLabel(squaresBetween3D(r.from, 0, r.to, climb, scene.distance.diagonal), scene.distance)
      : rulerLabel(r.from, r.to, scene.distance, scene.difficult);
  t.style.fontSize = 12 * (getLook().text ?? 1);
  t.scale.set(1 / z);
  t.anchor.set(0.5, 0.5);
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  t.position.set(mid.x, mid.y);
  const w = (t.width + 20 / z); const h = (t.height + 10 / z);
  g.roundRect(mid.x - w / 2, mid.y - h / 2, w, h, h / 2).fill({ color: 0x0f0d0a }).stroke({ color: gold, width: 1 / z });
  t.visible = true;
}

/** The DM's planning drawings (issue 1033) show faded on the DM's own screen: theirs alone, not the table's. */
const PLANNING_ALPHA = 0.5;

/** One drawing's node, in board pixels: a pencil mark on the map, so it keeps its size under a zoom. */
function buildDrawing(d: Drawing): Container {
  const colour = hex(d.colour);
  if (d.kind === 'text') {
    const t = new Text({ text: d.text ?? '', style: { fill: colour, fontSize: d.size ?? 24, fontFamily: 'sans-serif', fontWeight: '600', stroke: { color: colour > 0x7fffff ? 0x0c0a09 : 0xfafaf9, width: Math.max(2, (d.size ?? 24) / 8) } } });
    t.position.set(d.points[0]!, d.points[1]!);
    t.label = `drawing:${d.id}`;
    if (d.planning) t.alpha = PLANNING_ALPHA;
    return t;
  }
  const g = new Graphics();
  traceDrawing(g, d);
  g.stroke({ color: colour, width: d.width, cap: 'round', join: 'round', alpha: 0.95 });
  g.label = `drawing:${d.id}`;
  if (d.planning) g.alpha = PLANNING_ALPHA;
  return g;
}

/** A drawing's path, not yet stroked: for its node and for the Erase highlight. */
function traceDrawing(g: Graphics, d: Pick<Drawing, 'kind' | 'points' | 'text' | 'size'> & { width?: number }): void {
  const p = d.points;
  if (d.kind === 'stroke' || d.kind === 'line') {
    g.moveTo(p[0]!, p[1]!);
    if (p.length === 4 && p[0] === p[2] && p[1] === p[3]) g.lineTo(p[0]! + 0.01, p[1]!); // a dot still shows
    for (let k = 2; k + 1 < p.length; k += 2) g.lineTo(p[k]!, p[k + 1]!);
    return;
  }
  if (d.kind === 'text') { const size = d.size ?? 24; g.rect(p[0]! - 3, p[1]! - 3, Math.max(1, (d.text ?? '').length) * size * 0.6 + 6, size * 1.25 + 6); return; }
  const b = shapeBox({ points: p } as Drawing);
  if (d.kind === 'rect') g.rect(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0);
  else g.ellipse((b.x0 + b.x1) / 2, (b.y0 + b.y1) / 2, Math.max(0.5, (b.x1 - b.x0) / 2), Math.max(0.5, (b.y1 - b.y0) / 2));
}

const sameDrawing = (a: Drawing, b: Drawing): boolean => a === b || (a.kind === b.kind && a.colour === b.colour && a.width === b.width && a.text === b.text && a.size === b.size && a.planning === b.planning
  && a.points.length === b.points.length && a.points.every((v, k) => v === b.points[k]));

/** The drawings the store holds: a node built for a new or changed one, the gone ones destroyed, the order kept. */
function syncDrawings(scene: Scene, drawings: Drawing[]): void {
  scene.drawings = drawings;
  const keep = new Set(drawings.map((d) => d.id));
  for (const [id, n] of scene.drawNodes) if (!keep.has(id)) { n.node.destroy(); scene.drawNodes.delete(id); }
  let changed = false;
  for (const d of drawings) {
    const have = scene.drawNodes.get(d.id);
    if (have && sameDrawing(have.d, d)) { have.d = d; continue; }
    if (have) have.node.destroy();
    scene.drawNodes.set(d.id, { d, node: buildDrawing(d) });
    changed = true;
  }
  const kids = scene.drawLayer.children;
  if (changed || kids.length !== drawings.length + 1 || drawings.some((d, k) => kids[k] !== scene.drawNodes.get(d.id)!.node)) {
    scene.drawLayer.removeChildren();
    for (const d of drawings) scene.drawLayer.addChild(scene.drawNodes.get(d.id)!.node);
    scene.drawLayer.addChild(scene.drawDraft);
  }
  if (scene.drawHover && !keep.has(scene.drawHover)) scene.drawHover = null;
  drawDrawDraft(scene);
}

/** The mark under the pen, and the Erase highlight. */
function drawDrawDraft(scene: Scene): void {
  scene.dirty = true;
  const g = scene.drawDraft;
  g.clear();
  const host = scene.app.canvas?.parentElement;
  if (host) host.setAttribute('data-draw-hover', scene.drawing && scene.drawHover ? scene.drawHover : '');
  if (!scene.drawing) return;
  const t = drawTool.get();
  const z = scene.view.zoom;
  const hovered = scene.drawHover ? scene.drawNodes.get(scene.drawHover)?.d : undefined;
  if (hovered) { traceDrawing(g, hovered); g.stroke({ color: 0xfbbf24, width: (hovered.kind === 'text' ? 2 : hovered.width) + 8 / z, alpha: 0.55, cap: 'round', join: 'round' }); }
  const press = scene.drawPress;
  // Save as stamp (issue 1034): the box being dragged, or drawn and waiting for its name, dashed in amber.
  const box = t.mode === 'stampBox' ? (press && press.points.length >= 2 ? { x0: press.points[0]!, y0: press.points[1]!, x1: press.points[press.points.length - 2]!, y1: press.points[press.points.length - 1]! } : t.box) : null;
  if (box) {
    const x0 = Math.min(box.x0, box.x1); const y0 = Math.min(box.y0, box.y1);
    g.rect(x0, y0, Math.abs(box.x1 - box.x0), Math.abs(box.y1 - box.y0));
    g.fill({ color: 0xfbbf24, alpha: 0.08 });
    g.stroke({ color: 0xfbbf24, width: 2 / z, alpha: 0.9 });
    return;
  }
  if (!press || press.points.length < 2 || t.mode === 'erase' || t.mode === 'text' || t.mode === 'stamp' || t.mode === 'stampBox') return;
  const pts = press.points.length === 2 ? [...press.points, ...press.points] : press.points;
  traceDrawing(g, { kind: t.mode, points: t.mode === 'stroke' ? pts : [pts[0]!, pts[1]!, pts[pts.length - 2]!, pts[pts.length - 1]!] });
  g.stroke({ color: hex(t.colour || scene.ownHex), width: t.width, cap: 'round', join: 'round', alpha: 0.95 });
}

/** Why this viewer could not pick a token up during a fight (design/20 §4): the screen's "no", the server's is the protection. */
function turnRefusal(scene: Scene, id: string): string | null {
  return scene.encounter ? previewRefusal(scene.encounter, id, scene.isDm || scene.movesAny === true ? 'dm' : 'player') : null;
}

function drawFloor(floor: Graphics, extent: Extent, hasMap: boolean): void {
  floor.clear();
  if (hasMap) return;
  floor.rect(0, 0, extent.width, extent.height).fill({ color: parseInt(BOARD_BG[getLook().theme].slice(1), 16) });
  floor.rect(0, 0, extent.width, extent.height).stroke({ color: 0x78716c, width: 2 });
}

const hex = (colour: string) => parseInt(colour.slice(1), 16);
const commitRef: { current: ((grid: Grid) => void) | null } = { current: null };

/** The grid in board coordinates: each axis derived over its extent, positions mirrored for a far edge. */
function drawGrid(scene: Scene, grid: Grid, isDm: boolean, linesOnly = false): void {
  scene.dirty = true;
  const { grid: g, handles, extent, editing } = scene;
  g.clear();
  // Mid-drag every frame redraws the lines alone; the padlocks and grab strips are rebuilt on release.
  if (!linesOnly) handles.removeChildren().forEach((c) => c.destroy({ children: true }));
  if (!grid.appearance.visible && !editing) return;
  // No grid at all (issue 1035, GRID-005): no line, no handle.
  if (isGridless(grid)) return;
  const blanks = grid.blank ?? [];
  const colour = hex(grid.appearance.colour);
  const alpha = grid.appearance.visible ? grid.appearance.opacity : 0.25;
  const xs = deriveAxis(grid.x, extent.width);
  const ys = deriveAxis(grid.y, extent.height);
  for (const l of xs) {
    const x = toBoard(grid.x.edge, l.pos, extent.width);
    // Round the areas left ungridded (issue 1035, GRID-008).
    for (const [a, b] of lineSegments('x', x, extent.height, blanks)) g.moveTo(x, a).lineTo(x, b);
    g.stroke({ color: l.locked ? 0x22c55e : l.provisional ? 0xf59e0b : colour, width: l.locked || l.provisional ? 2 : 1, alpha: l.locked || l.provisional ? Math.max(alpha, 0.9) : alpha });
  }
  for (const l of ys) {
    const y = toBoard(grid.y.edge, l.pos, extent.height);
    for (const [a, b] of lineSegments('y', y, extent.width, blanks)) g.moveTo(a, y).lineTo(b, y);
    g.stroke({ color: l.locked ? 0x22c55e : l.provisional ? 0xf59e0b : colour, width: l.locked || l.provisional ? 2 : 1, alpha: l.locked || l.provisional ? Math.max(alpha, 0.9) : alpha });
  }
  if (!editing || !isDm || linesOnly) return;
  // The areas left ungridded, outlined while setting up (issue 1035).
  for (const b of blanks) g.rect(b.x, b.y, b.width, b.height).stroke({ color: 0xf59e0b, width: 2 / scene.view.zoom, alpha: 0.9 });
  // Drawing one, the lines take no press: the drag is the area's.
  if (gridEditor.get().drawingBlank) return;
  // The editor's furniture: a padlock at the source edge of every line, and a grab strip along each line.
  const padlock = (cx: number, cy: number, locked: boolean, axis: 'x' | 'y', index: number, pos: number) => {
    // Drawn about its own origin in screen pixels, placed in board units and scaled by the inverse zoom, so it keeps its size.
    const h = new Graphics();
    h.circle(0, 0, HANDLE_R).fill({ color: locked ? 0x16a34a : 0xdc2626 }).stroke({ color: 0xfafaf9, width: 1.5 });
    // A closed lock is a filled body under a shackle; an open lock lifts the shackle to one side.
    h.rect(-3.5, -0.5, 7, 5).fill({ color: 0xfafaf9 });
    if (locked) h.moveTo(-2, -0.5).lineTo(-2, -3).arc(0, -3, 2, Math.PI, 0).lineTo(2, -0.5).stroke({ color: 0xfafaf9, width: 1.2 });
    else h.moveTo(2, -0.5).lineTo(2, -3).arc(4, -3, 2, Math.PI, 0).stroke({ color: 0xfafaf9, width: 1.2 });
    h.position.set(cx, cy);
    h.scale.set(1 / scene.view.zoom);
    h.eventMode = 'static';
    h.cursor = 'pointer';
    h.label = `padlock:${axis}:${index}`;
    // The toggle acts on the press: the host captures the pointer on every press, so the release never
    // reaches a Pixi object — and a press on a padlock is the explicit click the design asks for.
    h.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      const current = scene.gridSource;
      if (!current) return;
      const axisState = current[axis];
      const next = locked ? unlockLine(axisState, index) : lockLine(axisState, index, pos);
      gridEditor.select({ axis, index });
      commitRef.current?.({ ...current, [axis]: next });
    });
    handles.addChild(h);
  };
  const strip = (axis: 'x' | 'y', index: number, locked: boolean, x: number, y: number, w: number, h: number) => {
    const s = new Graphics();
    s.rect(x, y, w, h).fill({ color: 0xffffff, alpha: 0.001 });
    s.eventMode = 'static';
    s.cursor = locked ? 'not-allowed' : axis === 'x' ? 'col-resize' : 'row-resize';
    s.label = `line:${axis}:${index}`;
    s.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      gridEditor.select({ axis, index });
      if (!locked) scene.dragging = { axis, index };
    });
    handles.addChild(s);
  };
  const half = 5 / scene.view.zoom;
  const r = HANDLE_R / scene.view.zoom;
  for (const l of xs) {
    const x = toBoard(grid.x.edge, l.pos, extent.width);
    strip('x', l.index, l.locked, x - half, 0, half * 2, extent.height);
    padlock(x, grid.y.edge === 'bottom' ? extent.height - r : r, l.locked, 'x', l.index, l.pos);
  }
  for (const l of ys) {
    const y = toBoard(grid.y.edge, l.pos, extent.height);
    strip('y', l.index, l.locked, 0, y - half, extent.width, half * 2);
    padlock(grid.x.edge === 'right' ? extent.width - r : r, y, l.locked, 'y', l.index, l.pos);
  }
}

// ── Things on the board ────────────────────────────────────────────────────

const textures = new Map<string, Promise<Texture | null>>();
// Animated tokens (issue 1176): the pictures that move, one player each, shared by every token wearing it. A frame a
// player drew sets `animationChanged`; the board's tick turns that into one dirty frame.
const movingByAsset = new Map<string, Moving<Texture>>();
let animationChanged = false;
const movingEnv: MovingEnv<Texture> = {
  videoTexture(video) {
    // Pixi's own ticker never drives it (its constructor turns autoUpdate on; it is turned off here): the board's
    // capped loop says when a frame is taken.
    const source = new VideoSource({ resource: video, autoPlay: false });
    source.autoUpdate = false;
    const texture = new Texture({ source });
    return { texture, update: () => source.update(), destroy: () => texture.destroy(false) };
  },
  canvasTexture(canvas) {
    const source = new CanvasSource({ resource: canvas });
    const texture = new Texture({ source });
    return { texture, update: () => source.update(), destroy: () => texture.destroy(false) };
  },
};
function textureFor(asset: string): Promise<Texture | null> {
  const have = textures.get(asset);
  if (have) return have;
  const loading = (async () => {
    // The bytes with the bearer (never a public URL); a picture that moves gets a player, any other an <img> as ever.
    const blob = await pictureBlob(asset);
    if (!blob) return null;
    const moving = await makeMoving(blob, movingEnv, () => { animationChanged = true; }).catch(() => null);
    if (moving) { movingByAsset.set(asset, moving); return moving.texture; }
    const url = await pictureUrl(asset);
    if (!url) return null;
    const img = new Image();
    img.src = url;
    await img.decode().catch(() => undefined);
    return Texture.from(img);
  })();
  textures.set(asset, loading);
  loading.then((t) => { if (!t) textures.delete(asset); });
  return loading;
}

// Stacking: scenery under tokens under the DM's things (`raiseOrder`). A player has no layer, but a prop is a prop — it sits under the tokens for them too.
const layerOf = (o: Thing): string => ('layer' in o ? o.layer : o.kind === 'prop' ? 'scenery' : 'token');

function nodeCentre(scene: Scene, id: string): Point | null {
  const t = scene.travels.get(id);
  if (!t) return null;
  const k = Math.min(1, (performance.now() - t.started) / t.ms);
  return pointAlong(t.route, k);
}

/** Place a node at its footprint: art stretched to it or fitted inside, name below, marks over. */
function layoutNode(scene: Scene, node: Container, o: Thing, at?: Point): void {
  scene.dirty = true;
  const grid = scene.objectsGrid;
  // What this layout drew from, so a redraw of the things can skip a node nothing changed for (see drawThings).
  // A layout at a point (a drag, a travel) is not where the thing stands: the next redraw always lays it out again.
  (node as ThingNode).laid = at ? null : layoutKey(scene, o);
  const centre = at ?? { x: o.x, y: o.y };
  const fp = footprintAt(grid, scene.extent, centre, o.size);
  // Fan-out (issue 1030): at rest only — a dragged or travelling thing is drawn where the hand or the route has it.
  const off = at ? undefined : scene.fan.get(o.id);
  node.position.set(fp.x + (off?.dx ?? 0), fp.y + (off?.dy ?? 0));
  const art = node.getChildByLabel('art') as Container;
  // Rotation (issue 1030, TOKEN-009): the art turns about its centre; the footprint, the marks and the name do not.
  const turn = 'rotation' in o && typeof o.rotation === 'number' ? o.rotation : 0;
  art.pivot.set(fp.width / 2, fp.height / 2); art.position.set(fp.width / 2, fp.height / 2);
  art.rotation = (turn * Math.PI) / 180;
  const sprite = art.getChildByLabel('sprite') as Sprite | null;
  const disc = art.getChildByLabel('disc') as Graphics;
  const hidden = 'hidden' in o && o.hidden;
  if (sprite) {
    if (o.fit === 'stretch') { sprite.position.set(0, 0); sprite.width = fp.width; sprite.height = fp.height; }
    else {
      const k = Math.min(fp.width / sprite.texture.width, fp.height / sprite.texture.height);
      sprite.width = sprite.texture.width * k; sprite.height = sprite.texture.height * k;
      sprite.position.set((fp.width - sprite.width) / 2, (fp.height - sprite.height) / 2);
    }
  } else {
    disc.clear();
    disc.ellipse(fp.width / 2, fp.height / 2, fp.width * 0.42, fp.height * 0.42).fill({ color: plainDiscColour(), alpha: hidden ? 0.5 : 0.9 }).stroke({ color: 0xfafaf9, width: 2 });
  }
  const initials = art.getChildByLabel('initials') as Text | null;
  if (initials) {
    const text = sprite || o.kind === 'prop' ? '' : initialsOf(o.name);
    initials.visible = text !== '';
    if (text) {
      initials.text = text;
      initials.style.fontSize = Math.max(6, Math.min(fp.width, fp.height) * (text.length > 1 ? 0.3 : 0.38));
      initials.position.set(fp.width / 2, fp.height / 2);
    }
  }
  const label = node.getChildByLabel('name') as Text;
  // The same size on screen at every zoom (issue 787): the old cap of 16 board pixels made a name
  // about 4 px tall at fit-to-screen. Where the token itself is too small on screen to carry a
  // label, the plate goes rather than shrinks — the overlays' rule (design/19 §5).
  const z = scene.view.zoom;
  label.position.set(fp.width / 2, fp.height + 2 / z);
  // Drawn at its screen size and counter-scaled against the zoom, so it is rasterised at the
  // size it shows and stays sharp close up (a resolution change alone does not re-draw a Pixi text).
  label.style.fontSize = NAMEPLATE_PX * (getLook().text ?? 1);
  label.scale.set(1 / z);
  // The nameplate is per token, the DM's: hidden renders nothing, not a placeholder (design/19 §3).
  // A withheld name (COMBAT-007) is the same: absent from the projection, so no plate.
  label.visible = o.kind !== 'prop' && o.nameplate && o.name !== undefined && fp.width * z >= HUD_GONE_PX;
  limitHitToFootprint(node, fp.width, fp.height);
  drawOverlays(scene, node, o, fp);
  const marks = node.getChildByLabel('marks') as Graphics;
  marks.clear();
  // Dead (issue 855): greyed and crossed, still on the board and in the turn order. Hidden and dead is fainter still.
  node.alpha = hidden ? (o.dead ? 0.35 : 0.5) : o.dead ? 0.6 : 1;
  // Only on a change (issue 857): an assignment on every layout, even of an empty list, made a filter effect per node.
  const greyed = !!art.filters && art.filters.length > 0;
  if (greyed !== !!o.dead) art.filters = o.dead ? [deadFilter()] : null;
  if (o.dead) {
    const inset = Math.min(fp.width, fp.height) * 0.12; const w = Math.max(3, Math.min(fp.width * 0.08, 6 / scene.view.zoom));
    marks.moveTo(inset, inset).lineTo(fp.width - inset, fp.height - inset).moveTo(fp.width - inset, inset).lineTo(inset, fp.height - inset).stroke({ color: 0x7f1d1d, width: w, cap: 'round' });
  }
  // The side (qa-runs#1157): a thin outline just inside the token's own edge — green, blue or red in this person's
  // palette — so the selection's gold ring sits on the edge over it and the marker's ring stays outside the footprint.
  const side = sideOutline(o as { kind: string }, scene.sides ?? 'none');
  if (side) {
    const z = scene.view.zoom; const w = 2 / z; const inset = 1.5 / z + w / 2;
    marks.ellipse(fp.width / 2, fp.height / 2, Math.max(1, fp.width / 2 - inset), Math.max(1, fp.height / 2 - inset)).stroke({ color: sideColour(side), width: w });
  }
  // The marker (issue 855): a coloured ring round the footprint, outside the selection's box.
  if (o.marker) {
    const z = scene.view.zoom; const pad = 3 / z;
    marks.ellipse(fp.width / 2, fp.height / 2, fp.width / 2 + pad, fp.height / 2 + pad).stroke({ color: markerColour(o.marker), width: 3 / z });
  }
  // Selected (issue 1541): a gold ring on the footprint's edge and a dashed gold halo outside it.
  if (selection.get().has(o.id)) {
    const z = scene.view.zoom; const gold = boardGold();
    marks.ellipse(fp.width / 2, fp.height / 2, fp.width / 2, fp.height / 2).stroke({ color: gold, width: 3 / z });
    dashedCircle(marks, fp.width / 2, fp.height / 2, Math.max(fp.width, fp.height) / 2 + 8 / z, 4 / z, 4 / z);
    marks.stroke({ color: gold, width: 1.5 / z });
  }
  // Tap to move (issue 1178): the token waiting for its square wears a second, sky ring outside the selection's.
  if (tapMoveStore.get() === o.id) { const z = scene.view.zoom; marks.rect(-7 / z, -7 / z, fp.width + 14 / z, fp.height + 14 / z).stroke({ color: 0x38bdf8, width: 3 / z }); }
  if (o.locked) {
    const s = 12 / scene.view.zoom;
    marks.rect(fp.width - s - 2, 2, s, s).fill({ color: 0x0c0a09, alpha: 0.7 });
    marks.rect(fp.width - s * 0.8 - 2, 2 + s * 0.45, s * 0.6, s * 0.4).fill({ color: 0xfafaf9 });
  }
  // A Toggle on (qa-runs#371): a glowing ring just outside the footprint, inside the marker's and the turn marker's.
  const ring = scene.toggleRings?.get(o.id);
  if (ring !== undefined) {
    const z = scene.view.zoom; const pad = 1.5 / z;
    marks.ellipse(fp.width / 2, fp.height / 2, fp.width / 2 + pad, fp.height / 2 + pad).stroke({ color: ring, width: 2 / z, alpha: 0.95 });
    marks.ellipse(fp.width / 2, fp.height / 2, fp.width / 2 + pad + 2 / z, fp.height / 2 + pad + 2 / z).stroke({ color: ring, width: 3 / z, alpha: 0.3 });
  }
  // The turn marker (design/20 §5): one neutral style for every turn, a ring and a pointer above, on the token acting now.
  if (scene.turnTokens.has(o.id)) {
    const z = scene.view.zoom;
    marks.roundRect(-5 / z, -5 / z, fp.width + 10 / z, fp.height + 10 / z, 6 / z).stroke({ color: 0xfafaf9, width: 3 / z });
    const t = 9 / z;
    marks.poly([fp.width / 2 - t, -8 / z - t * 1.6, fp.width / 2 + t, -8 / z - t * 1.6, fp.width / 2, -8 / z]).fill({ color: 0xfafaf9 }).stroke({ color: 0x0c0a09, width: 1 / z });
  }
}

/**
 * The overlays (design/19 §4, §5): health (HP) as a red bar with white numerals, the defence (AC) as a
 * shield with its value, two slots — a bar when the slot has a maximum, a chip
 * when it does not, the label on hover. Drawn from the values THIS viewer was
 * given (the projection's whitelist), only for the overlays the table has on,
 * scaled to the footprint. At low zoom the numerals go first and the bar
 * stays; lower still, everything goes — a smear of chrome is worse than none.
 */
const HUD_NUMERALS_PX = 44;
const HUD_GONE_PX = 22;
function drawOverlays(scene: Scene, node: Container, o: Thing, fp: { width: number; height: number }): void {
  const hud = node.getChildByLabel('hud') as Container;
  const px = fp.width * scene.view.zoom;
  const on = scene.overlays;
  const v = o.values;
  const gone = o.kind === 'prop' || px < HUD_GONE_PX;
  hud.visible = !gone;
  if (gone) return;
  const numerals = px >= HUD_NUMERALS_PX;
  const z = scene.view.zoom;
  const font = Math.max(8, Math.min(13, 11 / Math.max(0.35, z)));
  const barH = Math.max(4, Math.min(fp.height * 0.14, 9 / z));
  const bar = (g: Graphics, t: Text, y: number, colour: number, current: number, max: number, text: string, label: () => string) => {
    g.clear();
    g.visible = true;
    g.roundRect(0, y, fp.width, barH, barH / 2).fill({ color: 0x0c0a09, alpha: 0.75 });
    const k = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
    if (k > 0) g.roundRect(0, y, fp.width * k, barH, barH / 2).fill({ color: colour });
    g.eventMode = 'static';
    t.text = text;
    t.style.fontSize = font;
    t.position.set(fp.width / 2, y + barH / 2);
    t.visible = numerals;
    g.removeAllListeners();
    g.on('pointerover', () => tipAt(label(), y));
    g.on('pointerout', () => hideTip());
  };
  const tip = hud.getChildByLabel('tip') as Text;
  const tipAt = (text: string, y: number) => { scene.dirty = true; tip.text = text; tip.style.fontSize = font; tip.position.set(fp.width / 2, y - font * 0.9); tip.visible = true; };
  const hideTip = () => { if (tip.visible) scene.dirty = true; tip.visible = false; };
  hideTip();
  const hpBar = hud.getChildByLabel('hpbar') as Graphics; const hpText = hud.getChildByLabel('hptext') as Text;
  // The HP bar sits along the top edge of the footprint, inside it, so a token on the board's top row keeps its bar.
  // On a system whose health is one of its own resources (issue 1312), that resource, by its own label.
  const health = healthOf(v, scene.health, scene.words);
  const plate = node.getChildByLabel('name') as Text;
  // A creature the DM runs wears small pips under its footprint instead (issue 1541): the site's HP red on its dark
  // track, the numbers on hover. The same values the bar would draw from, so nothing a viewer was not sent.
  const pips = on.hp && health && wearsHpPips(o as { kind: string; pc?: boolean; owners?: string[] }, health);
  const pipH = Math.max(3, Math.min(fp.height * 0.1, 5 / z));
  if (pips && health) {
    hpBar.clear(); hpBar.visible = true; hpText.visible = false;
    const w = fp.width * 0.72; const x = (fp.width - w) / 2; const y = fp.height + 3 / z;
    const k = Math.max(0, Math.min(1, health.current / health.max));
    hpBar.roundRect(x, y, w, pipH, pipH / 2).fill({ color: 0x3a2422 });
    if (k > 0) hpBar.roundRect(x, y, w * k, pipH, pipH / 2).fill({ color: 0xb5403f });
    hpBar.eventMode = 'static'; hpBar.removeAllListeners();
    const words = `${health.current} / ${health.max}`;
    hpBar.on('pointerover', () => tipAt(`${healthOf(v, scene.health, scene.words)?.label ?? scene.words.health} ${words}`, y)); hpBar.on('pointerout', hideTip);
    // The nameplate moves down past the pips.
    plate.position.set(fp.width / 2, y + pipH + 2 / z);
  } else if (on.hp && health) bar(hpBar, hpText, 1 / z, 0xb5403f, health.current, health.max, `${health.current} / ${health.max}`, () => healthOf(v, scene.health, scene.words)?.label ?? scene.words.health);
  else { hpBar.clear(); hpBar.visible = false; hpText.visible = false; hpBar.removeAllListeners(); }
  const ac = hud.getChildByLabel('ac') as Graphics; const acText = hud.getChildByLabel('actext') as Text;
  if (on.ac && v.ac !== undefined) {
    const s = Math.max(10, Math.min(fp.width * 0.3, 22 / z));
    ac.clear(); ac.visible = true;
    // A shield: flat top, pointed bottom.
    ac.poly([0, 0, s, 0, s, s * 0.55, s / 2, s, 0, s * 0.55]).fill({ color: 0x1e3a8a, alpha: 0.95 }).stroke({ color: 0xfafaf9, width: 1 / z });
    ac.position.set(-s * 0.25, -s * 0.25);
    acText.text = String(v.ac); acText.style.fontSize = Math.max(7, s * 0.5); acText.position.set(-s * 0.25 + s / 2, -s * 0.25 + s * 0.42); acText.visible = numerals;
    ac.eventMode = 'static'; ac.removeAllListeners(); ac.on('pointerover', () => tipAt(scene.words.defence, -s * 0.25)); ac.on('pointerout', hideTip);
  } else { ac.clear(); ac.visible = false; acText.visible = false; ac.removeAllListeners(); }
  let slotY = (pips ? fp.height + 3 / z + pipH + 2 / z : fp.height + 2 / z) + (plate.visible ? plate.height + 2 / z : 0);
  for (const key of ['slot1', 'slot2'] as const) {
    const g = hud.getChildByLabel(key) as Graphics; const t = hud.getChildByLabel(`${key}text`) as Text;
    const slot = v[key];
    if (!on[key] || !slot) { g.clear(); g.visible = false; t.visible = false; g.removeAllListeners(); continue; }
    if (slot.max !== undefined) {
      bar(g, t, slotY, 0x0891b2, slot.value, slot.max, `${slot.value} / ${slot.max}`, () => slot.label);
      slotY += barH + 2 / z;
    } else {
      // A chip: a small rounded plate at the token's foot with the value; the label on hover.
      const w = Math.max(14, Math.min(fp.width * 0.4, 30 / z)); const h = barH * 1.6;
      g.clear(); g.visible = true;
      g.roundRect(fp.width - w, slotY, w, h, h / 2).fill({ color: 0x374151, alpha: 0.95 }).stroke({ color: 0xfafaf9, width: 1 / z });
      t.text = String(slot.value); t.style.fontSize = font; t.position.set(fp.width - w / 2, slotY + h / 2); t.visible = numerals;
      g.eventMode = 'static'; g.removeAllListeners(); g.on('pointerover', () => tipAt(slot.label, slotY)); g.on('pointerout', hideTip);
      slotY += h + 2 / z;
    }
  }
  drawConditions(scene, hud, o, fp, numerals, tipAt, hideTip);
}
/** One grey filter for every dead thing: built on first use, so nothing is made while nobody is dead. */
let sharedDeadFilter: ColorMatrixFilter | null = null;
function deadFilter(): ColorMatrixFilter {
  if (!sharedDeadFilter) { sharedDeadFilter = new ColorMatrixFilter(); sharedDeadFilter.desaturate(); }
  return sharedDeadFilter;
}

/**
 * Condition badges along the lower edge, inside the footprint (issue 855): a few, abbreviated as the on-screen size
 * allows, the rest one "+N"; the whole list on hover. The same thresholds as the other overlays: below the numerals'
 * size the badges are plain pips with no text; below the gone size, nothing (the hud is hidden already).
 */
function drawConditions(scene: Scene, hud: Container, o: Thing, fp: { width: number; height: number }, numerals: boolean, tipAt: (text: string, y: number) => void, hideTip: () => void): void {
  const g = hud.getChildByLabel('conds') as Graphics;
  const texts = hud.getChildByLabel('condtexts') as Container;
  const z = scene.view.zoom;
  const px = fp.width * z;
  const badges = scene.overlays.conditions === false ? null : conditionBadges([...shownConditions(o.values, woundsOf(scene.wounds, o.values.resources).words), ...(o.concentrating ? [CONCENTRATING_BADGE] : [])], px >= 120);
  // Redrawn only when what the badges show changes (issue 857): a token with no conditions was cleared, stripped of its
  // listeners and emptied of texts on every layout. The key is everything the drawing below reads.
  const key = badges ? JSON.stringify([badges, fp.width, fp.height, z, numerals]) : '';
  const held = hud as Container & { condKey?: string };
  if (held.condKey === key) return;
  held.condKey = key;
  for (const t of texts.removeChildren()) t.destroy();
  g.clear(); g.removeAllListeners();
  if (!badges) { g.visible = false; texts.visible = false; return; }
  texts.visible = true;
  g.visible = true;
  const places = badges.shown.length + (badges.more ? 1 : 0);
  const gap = 2 / z;
  const h = Math.max(fp.height * 0.08, Math.min(fp.height * 0.2, 14 / z));
  const w = (fp.width - gap * (places + 1)) / places;
  const y = fp.height - h - gap;
  const font = Math.max(6, Math.min(h * 0.75, 11 / Math.max(0.35, z)));
  const labels = [...badges.shown, ...(badges.more ? [`+${badges.more}`] : [])];
  labels.forEach((label, k) => {
    const x = gap + k * (w + gap);
    const extra = badges.more > 0 && k === labels.length - 1;
    g.roundRect(x, y, w, h, Math.min(h, w) / 3).fill({ color: extra ? 0x374151 : 0x92400e, alpha: 0.92 }).stroke({ color: 0xfafaf9, width: 1 / z });
    if (!numerals) return;
    const t = new Text({ text: label, style: { fill: 0xfafaf9, fontSize: font, fontWeight: 'bold', fontFamily: 'sans-serif' } });
    t.anchor.set(0.5, 0.5); t.position.set(x + w / 2, y + h / 2);
    if (t.width > w) t.scale.set(w / t.width);
    texts.addChild(t);
  });
  g.eventMode = 'static';
  g.on('pointerover', () => tipAt(badges.full, y));
  g.on('pointerout', hideTip);
}

/** Build a node for a thing: its art (or a disc), its name, its marks; placed by `layoutNode`. */
function buildNode(scene: Scene, o: Thing, mayMove: boolean): ThingNode {
  const node: ThingNode = new Container();
  node.label = `thing:${o.id}`;
  node.asset = o.asset;
  const id = o.id;
  const hidden = 'hidden' in o && o.hidden;
  const art = new Container();
  art.label = 'art';
  const disc = new Graphics();
  disc.label = 'disc';
  // A plain disc's initials (issue 1541), in the frame's Cinzel: laid out with the disc, hidden under a picture.
  const initials = new Text({ text: '', style: { fill: 0xf2ecd8, fontSize: 16, fontFamily: 'Cinzel, Georgia, serif', fontWeight: '700' } });
  initials.label = 'initials';
  initials.anchor.set(0.5, 0.5);
  initials.visible = false;
  art.addChild(disc, initials);
  const label = new Text({ text: o.name ?? '', style: { fill: 0xfafaf9, fontSize: 13, fontFamily: 'sans-serif', stroke: { color: 0x0c0a09, width: 3 } } });
  label.label = 'name';
  // On whole screen pixels: at some zooms a plate fell between pixels and drew soft and grey (2026-10-05, 1920x1080).
  label.roundPixels = true;
  label.anchor.set(0.5, 0);
  const marks = new Graphics();
  marks.label = 'marks';
  const hud = new Container();
  hud.label = 'hud';
  const numeral = () => { const t = new Text({ text: '', style: { fill: 0xfafaf9, fontSize: 11, fontWeight: 'bold', fontFamily: 'sans-serif', stroke: { color: 0x0c0a09, width: 2 } } }); t.anchor.set(0.5, 0.5); return t; };
  for (const name of ['hpbar', 'ac', 'slot1', 'slot2', 'conds']) { const g = new Graphics(); g.label = name; hud.addChild(g); }
  const condTexts = new Container(); condTexts.label = 'condtexts'; hud.addChild(condTexts);
  for (const name of ['hptext', 'actext', 'slot1text', 'slot2text', 'tip']) { const t = numeral(); t.label = name; hud.addChild(t); }
  node.addChild(art, label, hud, marks);
  node.alpha = hidden ? 0.5 : 1;
  node.eventMode = 'static';
  refreshNode(scene, node, o, mayMove);
  // Its hover card (issue 1127): the numbers this viewer was sent, and the owner's actions. Not while a tool has the board.
  node.on('pointerover', (e: FederatedPointerEvent) => {
    if (scene.editing || scene.pointing || scene.measuring || scene.areaMode || scene.fogging || scene.zoning || scene.drawing || scene.tokenDrag || scene.targeting || scene.explaining) return;
    // A finger has no hover (issue 1178): a card that opened on every tap would cover the board. A tap selects instead.
    if (e.pointerType === 'touch') return;
    boardBus.hoverToken({ id, at: { x: e.clientX, y: e.clientY } });
  });
  node.on('pointerout', () => boardBus.hoverToken(null));
  node.on('pointerdown', (e: FederatedPointerEvent) => {
    // Picking a target from the action bar (issue 1130): the tap is the target's, and only that.
    if (scene.targeting && e.button === 0) { e.stopPropagation(); scene.pressed = true; targetTool.pick(id); return; }
    // "What's this?" (issue 1155): the tap opens the token's wiki card, and only that.
    if (scene.explaining && e.button === 0) { e.stopPropagation(); scene.pressed = true; whatsThisTool.pick(id, e.clientX, e.clientY); return; }
    if (scene.editing || scene.pointing || scene.measuring || scene.areaMode || scene.fogging || scene.zoning || scene.drawing) return; // grid setup has the lines; pointing, measuring and laying an area have the squares
    // A finger (issue 1178): the host's gestures decide tap, drag, pan or long press; the node only says it was hit.
    if (e.pointerType === 'touch' && !scene.walling) { scene.touchToken = id; return; }
    // The node outlives a change to its thing (drawThings reuses it): read the thing as it stands now.
    const o = scene.objects[id];
    if (!o) return;
    const mayMove = node.mayMove === true;
    // A click brings it to the front of its layer, on this screen only (issue 1030): a stack's every member is reachable.
    scene.raised.set(o.id, ++scene.raiseCount);
    restack(scene, raiseOrder(Object.values(scene.objects), scene.raised, layerOf));
    scene.dirty = true;
    // The right button opens the token's menu (issue 812): it selects the thing and nothing else — no drag, no pan.
    if (e.button === 2) {
      e.stopPropagation();
      const selected = [...selection.get()];
      selection.only(o.id);
      boardBus.openTokenMenu({ id: o.id, x: e.clientX, y: e.clientY, selected });
      return;
    }
    e.stopPropagation();
    scene.pressed = true; // the host's handler runs after this one: the press is taken even when nothing drags
    if ((e.shiftKey || e.ctrlKey || e.metaKey) && scene.isDm) selection.toggle(o.id); // shift or ctrl: several, across layers
    else if (!selection.get().has(o.id)) selection.only(o.id);
    if (o.locked) { scene.say(`${o.name ?? 'That token'} is locked.`); return; }
    // Drawing walls (issue 1144), a press on a token is a corner or a pick, never a drag: the host's handler takes it.
    if (scene.walling) { scene.pressed = false; return; }
    if (!mayMove) { scene.notMine = { x: e.clientX, y: e.clientY, name: o.name, said: false }; return; }
    pickUp(scene, o, node);
  });
  if (o.asset) {
    void textureFor(o.asset).then((t) => {
      if (!t || scene.destroyed || node.destroyed) return;
      const sprite = new Sprite(t);
      sprite.label = 'sprite';
      art.addChildAt(sprite, 0);
      disc.visible = false;
      const now = scene.objects[id];
      if (now) layoutNode(scene, node, now, nodeCentre(scene, id) ?? undefined);
    });
  }
  return node;
}

/** The hand (or a finger, issue 1178) picks a token of its own up: refused during someone else's turn, a group along. */
function pickUp(scene: Scene, o: Thing, node: ThingNode): void {
  // A refused move never starts (design/20 §4.1): the note says it is not your turn, never whose.
  const refusal = turnRefusal(scene, o.id);
  if (refusal) { scene.say(refusal); return; }
  // Several selected (the DM's, design/11 §5): the others of its kind come along, a locked one stays (issue 830).
  const group = scene.isDm && selection.get().size > 1
    ? groupOf(scene.objects as unknown as Record<string, GroupThing>, selection.get(), o.id)
    : { followers: [], lockedStayed: 0 };
  // The count the hand picked up (design/21 §4.2), as each follower's is: see `madeAgainst` at the release.
  scene.tokenDrag = { id: o.id, route: [{ x: o.x, y: o.y }], size: { ...o.size }, followers: group.followers, lockedStayed: group.lockedStayed, rev: moveRev(o) };
  node.cursor = 'grabbing';
}

/** A node's facts that change without its being rebuilt: its name, who may move it and the cursor that says so. */
function refreshNode(scene: Scene, node: ThingNode, o: Thing, mayMove: boolean): void {
  node.mayMove = mayMove;
  // The plate's text is resolvePlates' to set, every drawn frame: the whole name, or cut where it meets a neighbour's.
  // During a fight the cursor already says a token of yours cannot be picked up (design/20 Q5: both the cursor and the note).
  const withheld = mayMove && turnRefusal(scene, o.id) !== null;
  if (scene.tokenDrag?.id !== o.id) node.cursor = o.locked || withheld ? 'not-allowed' : mayMove ? 'grab' : 'pointer';
}

/** How long a press is held still to ping, and how far the hand may wander meanwhile (issue 1039). */
const PING_HOLD_MS = 450;
const PING_SLOP_PX = 6;

/** The pings in flight: a ring widening and fading in its maker's colour, the same size on screen at every zoom. */
function drawPings(scene: Scene, now: number): void {
  const g = scene.pingLayer;
  g.clear();
  const z = scene.view.zoom;
  scene.pings = scene.pings.filter((p) => now - p.started < PING_MS);
  for (const p of scene.pings) {
    const k = (now - p.started) / PING_MS;
    for (const lag of [0, 0.25]) {
      const t = k - lag;
      if (t < 0 || t > 1) continue;
      g.circle(p.x, p.y, (8 + 34 * t) / z).stroke({ color: p.colour, width: 3 / z, alpha: 1 - t });
    }
    g.circle(p.x, p.y, 4 / z).fill({ color: p.colour, alpha: 1 - k });
  }
  scene.dirty = true;
}

/** A plate's width on screen for a text, measured once per text (the plate is drawn at a fixed screen size). */
const plateWidths = new Map<string, number>();
function plateWidth(text: string, label: Text): number {
  let w = plateWidths.get(text);
  if (w === undefined) {
    w = CanvasTextMetrics.measureText(text, label.style).width;
    if (plateWidths.size > 5000) plateWidths.clear();
    plateWidths.set(text, w);
  }
  return w;
}

/**
 * Nameplates side by side (2026-10-05): at fit-to-screen two tokens a square apart drew their names as one smear. Each
 * shown plate keeps its whole name unless it would run into a neighbour's on its row; then the two share the room and
 * the cut name ends in "…" (`plates.ts`). Run before each drawn frame; a text is set only when it changes.
 */
function resolvePlates(scene: Scene): void {
  const z = scene.view.zoom;
  const plates: Plate[] = []; const shown: Array<[string, Text, string]> = [];
  for (const [id, node] of scene.nodes) {
    const label = node.getChildByLabel('name') as Text | null;
    if (!label) continue;
    const full = scene.objects[id]?.name ?? '';
    if (!label.visible || !node.visible) { if (label.text !== full) label.text = full; continue; }
    const top = (node.y + label.y) * z;
    plates.push({ id, cx: (node.x + label.x) * z, top, bottom: top + NAMEPLATE_PX * (getLook().text ?? 1) * 1.4, width: plateWidth(full, label) });
    shown.push([id, label, full]);
  }
  const limits = plateLimits(plates);
  for (const [id, label, full] of shown) {
    const max = limits.get(id);
    const text = max === undefined ? full : fitName(full, max, (t) => plateWidth(t, label));
    if (label.text !== text) label.text = text;
  }
}

/** The things on the floor this person is looking at (qa-runs#1160; design/32 §5): every thing with one floor. */
function floorThings(state: { objects: Record<string, unknown>; scene: { floors?: Floor[] | undefined } }, meId: string, isDm: boolean): Record<string, Thing> {
  const objects = state.objects as Record<string, Thing & { floor?: string }>;
  return onFloor(objects, state.scene.floors, viewedFloor({ scene: state.scene, objects }, meId, isDm));
}
/** The openings to draw on the floor in view (slice B): its own (onto the floor below) and the floor above's (onto it). */
function openingRows(floors: Floor[] | undefined, floor: string | null): Array<{ j: number; i0: number; i1: number }> {
  if (!floors || floors.length < 2 || !floor) return [];
  const at = floors.findIndex((f) => f.id === floor);
  const keys = [...(floors[at]?.openings ?? []), ...(floors[at + 1]?.openings ?? [])];
  return keys.map((k) => { const [i, j] = k.split(',').map(Number) as [number, number]; return { j, i0: i, i1: i }; });
}

/** The DM's walls on the floor in view (design/32 §1): each floor has its own. */
function floorWalls(state: { objects: Record<string, unknown>; scene: { floors?: Floor[] | undefined }; walls?: unknown }, meId: string, isDm: boolean): Wall[] {
  const floor = viewedFloor({ scene: state.scene, objects: state.objects as Record<string, { floor?: string }> }, meId, isDm);
  return wallsOnFloor((state.walls ?? []) as Wall[], floor ?? undefined, state.scene);
}

/** Every thing the projection gave this person, on its layer, in order. */
function drawThings(scene: Scene, objects: Record<string, Thing>, isDm: boolean, meId: string): void {
  scene.dirty = true;
  scene.objects = objects;
  // Reconciled, not rebuilt: every node was destroyed and built again — five texts, six graphics and a listener each —
  // on EVERY accepted change, so one token moving re-made the whole board. A node is rebuilt only when it is gone or
  // its picture changed; one whose thing and surroundings are as they were when it was last laid out is not touched.
  for (const [id, node] of scene.nodes) {
    const o = objects[id];
    if (o && o.asset === node.asset) continue;
    node.destroy({ children: true }); scene.nodes.delete(id);
  }
  // Fan-out (issue 1030): from this viewer's store alone, at the things' stored positions.
  scene.fan = fanOut(Object.values(objects).map((o) => ({ id: o.id, layer: layerOf(o), rect: footprintAt(scene.objectsGrid, scene.extent, { x: o.x, y: o.y }, o.size) })));
  const sorted = raiseOrder(Object.values(objects), scene.raised, layerOf);
  for (const o of sorted) {
    const mayMove = isDm || scene.movesAny === true || ('mine' in o && o.mine) || ('owners' in o && o.owners.includes(meId));
    let node = scene.nodes.get(o.id);
    if (!node) { node = buildNode(scene, o, mayMove); scene.nodes.set(o.id, node); }
    else refreshNode(scene, node, o, mayMove);
    const dragging = scene.tokenDrag?.id === o.id ? scene.tokenDrag.route[scene.tokenDrag.route.length - 1] : scene.groupAt.get(o.id);
    const at = dragging ?? nodeCentre(scene, o.id) ?? undefined;
    if (at || !sameLayout(node.laid, layoutKey(scene, o))) layoutNode(scene, node, o, at);
  }
  restack(scene, sorted);
}

/** The stacking order, put right only when it is wrong. */
function restack(scene: Scene, sorted: Thing[]): void {
  const kids = scene.things.children;
  if (kids.length !== sorted.length || sorted.some((o, k) => kids[k] !== scene.nodes.get(o.id))) {
    scene.things.removeChildren();
    for (const o of sorted) scene.things.addChild(scene.nodes.get(o.id)!);
  }
}

/** A thing's node, with what it was built and last laid out from. */
type ThingNode = Container & { asset?: string | null; mayMove?: boolean; laid?: LayoutKey | null };
interface LayoutKey { o: Thing; zoom: number; grid: Grid; width: number; height: number; overlays: Overlays; selected: boolean; turn: boolean; fan: string; palette: string; ring: number | null }
const layoutKey = (scene: Scene, o: Thing): LayoutKey => ({
  o, zoom: scene.view.zoom, grid: scene.objectsGrid, width: scene.extent.width, height: scene.extent.height, overlays: scene.overlays,
  selected: selection.get().has(o.id), turn: scene.turnTokens.has(o.id), palette: `${getLook().palette}/${getLook().theme}/${getLook().text ?? 1}/${scene.sides ?? 'none'}`, fan: ((f) => (f ? `${f.dx},${f.dy}` : ''))(scene.fan.get(o.id)),
  ring: scene.toggleRings?.get(o.id) ?? null,
});
const sameLayout = (a: LayoutKey | null | undefined, b: LayoutKey): boolean => !!a && a.o === b.o && a.zoom === b.zoom && a.grid === b.grid
  && a.width === b.width && a.height === b.height && a.overlays === b.overlays && a.selected === b.selected && a.turn === b.turn && a.fan === b.fan && a.palette === b.palette && a.ring === b.ring;

/** The map's picture, the one it replaces destroyed with its GPU copy (it is the map's alone). */
function setMapTexture(scene: Scene, texture: Texture): void {
  const previous = scene.map.texture;
  scene.map.texture = texture;
  if (previous && previous !== texture && previous !== Texture.EMPTY && previous !== Texture.WHITE && !previous.destroyed) previous.destroy(true);
}

function applyView(scene: Scene): void {
  scene.dirty = true;
  scene.world.scale.set(scene.view.zoom);
  scene.world.position.set(-scene.view.x * scene.view.zoom, -scene.view.y * scene.view.zoom);
}

/** How long after a change into or out of the phone layout (issue 1242) the board's resizes refit the map. */
const PHONE_REFIT_MS = 1000;

export function BoardCanvas() {
  // The floor in view (qa-runs#1160): a pick redraws the things and walls.
  const [pickedFloor, setPickedFloor] = useState(floorView.get());
  useEffect(() => floorView.subscribe(setPickedFloor), []);
  const { table, campaignId, settings, phone } = useShell();
  // The phone layout (issue 1242), read by the camera: a phone opens on the whole map, not on a view kept from a larger screen.
  const phoneRef = useRef(phone);
  phoneRef.current = phone;
  // This person's look (issue 1041): the site's theme and their palette, for every colour the board draws.
  // The text size (issue 1174) scales the nameplates; the rest of the board's numerals stay at their drawn size.
  const look = { palette: settings.palette ?? 'standard', theme: settings.theme ?? 'dark', text: (settings.textSize ?? 100) / 100 } as const;
  setLook(look);
  const { store, role, meId, act, travel, setNotice, viewingAs, ping, systemRules, powers } = useTableState();
  const movesAny = !!powers?.includes('tokens');
  // The system's words for a token's numbers (issue 1309): the overlays' hover labels read them as they show.
  const words = useNumberWords();
  const wordsRef = useRef(words);
  const health = useHealthResource();
  const healthRef = useRef(health);
  const woundRules = useWoundRules();
  const woundsRef = useRef(woundRules);
  // The system's distance rule (issue 1303), held for a board rebuilt later (View as and back): a new scene starts with it.
  const distanceRef = useRef<DistanceRules | null>(systemRules.distance ?? null);
  const reachRef = useRef(systemRules.reach);
  reachRef.current = systemRules.reach;
  const actionsRef = useRef(systemRules.actions);
  actionsRef.current = systemRules.actions;
  const pingRef = useRef(ping);
  pingRef.current = ping;
  const mapV = useMapVersion();
  // A player's map tiles (issue 1144, slice 3): named with their hashes while the vision or the manual fog is on.
  const tiles = useMapTiles();
  const tilesKey = tiles ? JSON.stringify(tiles) : null;
  const tilesOn = tilesKey !== null;
  const host = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<Scene | null>(null);
  // What the store's effects drew before the canvas was up, run as it is (issue 1420).
  const late = useRef(new LateScene<Scene>());
  /** Redraws the mover's area (qa-runs#1161): set once the canvas starts; a no-op before. */
  const refreshMoveRef = useRef<() => void>(() => {});
  const movesAnyRef = useRef(movesAny);
  movesAnyRef.current = movesAny;
  useEffect(() => { const sc = sceneRef.current; if (sc) { sc.movesAny = movesAny; sc.dirty = true; } }, [movesAny]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saved = useRef<BoardView | null>(null);
  // The scene on this screen (issue 834): the DM may be staging another than the players see.
  const map = useBoardMap();
  const colour = parseInt(identityColour(table.me.colour ?? '#d4af37').slice(1), 16);
  const ownHex = (table.me.colour ?? '#d4af37').toLowerCase();
  const ownHexRef = useRef(ownHex);
  ownHexRef.current = ownHex;
  const meRef = useRef({ id: meId, role });
  meRef.current = { id: meId, role };
  const isDm = role === 'dm';
  // The DM's board (`isDm`) is what is drawn; what may be SENT is the act's power (issue 1160): a co-DM shown the DM's
  // board without `tokens` or `map` gets none of the keys and clicks that send those.
  const mayAct = useMayAct();
  const mayActRef = useRef(mayAct);
  mayActRef.current = mayAct;
  const actRef = useRef(act);
  actRef.current = act;
  const sayRef = useRef(setNotice);
  sayRef.current = setNotice;
  const gridRef = useRef(store.state.grid);
  gridRef.current = store.state.grid;
  const storeRef = useRef(store);
  storeRef.current = store;
  const colourRef = useRef(colour);
  colourRef.current = colour;
  const fogFloorNow = viewedFloor({ scene: store.state.scene, objects: store.state.objects as Record<string, { floor?: string }> }, meId, isDm);
  const fogRef = useRef({ fog: store.state.fog ?? null, style: fogStyle(isDm ? 'dm' : 'player', viewingAs !== null), floor: fogFloorNow });
  fogRef.current = { fog: store.state.fog ?? null, style: fogStyle(isDm ? 'dm' : 'player', viewingAs !== null), floor: fogFloorNow };

  // Mount once per table; the application lives outside React.
  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    let live = true;
    const scene: Scene = {
      app: new Application(), world: new Container(), map: new Sprite(), floor: new Graphics(), grid: new Graphics(), handles: new Container(),
      things: new Container(), overlay: new Graphics(), view: { x: 0, y: 0, zoom: 1 }, extent: boardExtent(null), destroyed: false, gridSource: null,
      objectsGrid: gridRef.current, editing: false, isDm, movesAny: movesAnyRef.current, say: (text) => sayRef.current(text), dragging: null, tokenDrag: null, groupAt: new Map(), pressed: false, notMine: null, box: null, overlays: { ...DEFAULT_OVERLAYS }, words: wordsRef.current, health: healthRef.current, wounds: woundsRef.current,
      nodes: new Map(), objects: {}, snap: true, travels: new Map(), encounter: null, turnTokens: new Set(), fan: new Map(), raised: new Map(), raiseCount: 0,
      focusLayer: new Graphics(), pingLayer: new Graphics(), pings: [], fxLayer: new Graphics(), effects: [], focuses: {}, focusColours: {}, pointing: false, focusDrag: null, hovered: null, placing: false,
      areaLayer: new Graphics(), auraLayer: new Graphics(), moveLayer: new Graphics(), areas: [], areaMode: false, targeting: false, explaining: false, areaHover: null, distance: distanceRef.current,
      measureLayer: new Graphics(), measureText: new Text({ text: '', style: { fill: 0xe6d6ae, fontSize: 12, fontFamily: 'Inter, sans-serif', fontWeight: '600' } }), measuring: false, measureDrag: false,
      fogLayer: new Graphics(), fog: null, fogStyle: 'none', fogging: false, zones: [], zoning: false, dirty: true, wallLayer: new Graphics(), walls: [], doors: [], lights: [], walling: false, tileLayer: new Container(), tileBack: new Graphics(), tileSprites: new Map(), tilesOf: null,
      pyramid: new PyramidLayer(() => { scene.dirty = true; }, (id) => { if (host.current) host.current.dataset.map = id; }),
      drawLayer: new Container(), drawNodes: new Map(), drawDraft: new Graphics(), drawings: [], drawing: false, drawPress: null, drawHover: null, touches: new Map(), ownHex: ownHexRef.current,
    };
    const viewport = () => ({ width: el.clientWidth, height: el.clientHeight });
    const relayout = () => { for (const [id, node] of scene.nodes) { const o = scene.objects[id]; if (o) layoutNode(scene, node, o, nodeCentre(scene, id) ?? undefined); } };
    const settle = (next: BoardView) => {
      const zoomed = next.zoom !== scene.view.zoom;
      scene.view = next;
      applyView(scene);
      // The editor's furniture and the marks are sized in screen pixels: rebuilt on a zoom, never on a pan.
      if (zoomed) {
        if (scene.editing && scene.gridSource) drawGrid(scene, scene.gridSource, isDm);
        relayout();
        drawFocuses(scene, colourRef.current);
        drawAreas(scene, colourRef.current);
        drawRuler(scene);
        drawFog(scene);
        drawDrawDraft(scene);
        drawWalls(scene);
      }
      boardBus.viewChanged({ view: scene.view, viewport: viewport(), extent: scene.extent });
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        if (!sameView(saved.current, scene.view)) { saved.current = scene.view; void saveView(campaignId, scene.view); }
      }, SAVE_DELAY_MS);
    };
    commitRef.current = (grid: Grid) => { gridEditor.draft(null); actRef.current({ kind: 'grid.set', grid } as EventBody); };

    (async () => {
      await scene.app.init({ resizeTo: el, background: getLook().theme === 'light' ? 0xe7e5e4 : 0x14110e, antialias: true, resolution: window.devicePixelRatio || 1, autoDensity: true });
      if (!live) { scene.app.destroy(true); return; }
      el.appendChild(scene.app.canvas);
      scene.app.canvas.setAttribute('data-testid', 'board-canvas');
      scene.app.canvas.setAttribute('aria-label', 'The board');
      scene.app.canvas.setAttribute('role', 'img');
      scene.measureText.visible = false;
      // Cinzel arrives after the first frame (issue 1541; font-display swap): a Pixi text keeps the face it was drawn in,
      // so once it has loaded every token's initials are drawn again.
      void document.fonts?.load('700 16px Cinzel').then(() => {
        if (!live) return;
        for (const node of scene.nodes.values()) {
          const t = (node.getChildByLabel('art') as Container | null)?.getChildByLabel('initials') as Text | null;
          if (t?.text) { const was = t.text; t.text = ''; t.text = was; }
        }
        scene.dirty = true;
      }).catch(() => {});
      scene.tileLayer.addChild(scene.tileBack);
      // The DM's fog shade lies over the things, dimming what players cannot see. A player is sent only what she may see
      // (design/22, design/30 §4), so her darkness and unexplored shade go UNDER the things: drawn over them, her own
      // token vanished in a dark scene (the light spec on vtt-dev, 2026-10-10). Only the things and the handles are ever
      // hit: the shade over a fogged square took the DM's press on the prop standing there (issue 1326).
      stackWorld(scene.world, { ...scene, pyramid: scene.pyramid.container }, scene.isDm);
      scene.moveLayer.eventMode = 'none';
      scene.world.addChildAt(scene.moveLayer, scene.world.getChildIndex(scene.auraLayer) + 1);
      scene.app.stage.addChild(scene.world);
      scene.app.stage.eventMode = 'static';
      scene.app.stage.hitArea = scene.app.screen;
      sceneRef.current = scene;
      drawFloor(scene.floor, scene.extent, false);
      scene.gridSource = gridRef.current;
      scene.objectsGrid = gridRef.current;
      scene.snap = storeRef.current.state.scene.snap;
      drawGrid(scene, scene.gridSource, isDm);
      drawThings(scene, floorThings(storeRef.current.state, meId, isDm), isDm, meId);
      scene.fog = fogRef.current.fog; scene.fogStyle = fogRef.current.style; scene.floors = storeRef.current.state.scene.floors; scene.fogFloor = fogRef.current.floor; scene.fogging = isDm && fogTool.get().on; scene.zoning = isDm && zoneTool.get().on; scene.zones = isDm ? (storeRef.current.state.zones ?? []) as Zone[] : []; scene.difficult = difficultTest(storeRef.current.state);
      drawFog(scene);
      scene.drawLayer.addChild(scene.drawDraft);
      syncDrawings(scene, storeRef.current.state.drawings ?? []);
      scene.walls = isDm ? floorWalls(storeRef.current.state, meId, isDm) : []; scene.doors = doorMarks((storeRef.current.state.walls ?? []) as Array<Pick<Wall, 'a' | 'b' | 'door'> & { id?: string }>); scene.walling = isDm && wallTool.get().on; drawWalls(scene);
      // Everything else the state held on arrival — the focuses and areas, auras, lights, the turn's reading — as the
      // store's effects left it while the canvas started (issue 1420: a reloaded player's board listed a focus but drew none).
      late.current.ready(scene);

      // Where this person left it, or the whole board.
      const kept = await fetchView(campaignId);
      if (!live) return;
      saved.current = kept;
      // A first arrival's fit is kept too, so "the view you left" is defined from the first look.
      // A board with no size yet (its panel still being laid out, qa-runs 1199) takes that view once it has one.
      // In the phone layout (issue 1242) the whole map, always: a view kept from a larger screen showed a phone a few squares of it.
      const first = () => settle(kept && !phoneRef.current ? clampView(kept, scene.extent, viewport()) : fitToScreen(scene.extent, viewport()));
      let unsized = el.clientWidth <= 0 || el.clientHeight <= 0;
      if (!unsized) first();
      el.dispatchEvent(new CustomEvent('board-ready'));

      const boardPoint = (e: PointerEvent | MouseEvent) => {
        const r = el.getBoundingClientRect();
        return { x: scene.view.x + (e.clientX - r.left) / scene.view.zoom, y: scene.view.y + (e.clientY - r.top) / scene.view.zoom };
      };
      let drag: { x: number; y: number } | null = null;
      // Hold to ping (issue 1039, Tom's choice A): a press on the empty board held still for PING_HOLD_MS pings there.
      let hold: { x: number; y: number; timer: ReturnType<typeof setTimeout> } | null = null;
      const letGo = () => { if (hold) { clearTimeout(hold.timer); hold = null; } };
      /** A follower's square, when the scene snaps (design/10 §5). */
      const snapper = () => (scene.snap ? (p: Point, size: { w: number; h: number }) => snapToCells(scene.objectsGrid, scene.extent, p, size).centre : null);
      const cellUnder = (e: PointerEvent) => cellOf(scene.objectsGrid, scene.extent, boardPoint(e));
      // ── Drawing (issue 1032) ──
      const drawPoint = (e: PointerEvent): Pt => clampToBoard(boardPoint(e), scene.extent, scene.objectsGrid.x.basePitch);
      let pinch: { dist: number; mid: Pt } | null = null;
      const twoFingers = () => {
        const [a, b] = [...scene.touches.values()];
        const r = el.getBoundingClientRect();
        return { dist: Math.hypot(a!.x - b!.x, a!.y - b!.y), mid: { x: (a!.x + b!.x) / 2 - r.left, y: (a!.y + b!.y) / 2 - r.top } };
      };
      const drawDown = (e: PointerEvent) => {
        if (e.pointerType === 'touch') {
          scene.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (scene.touches.size >= 2) { scene.drawPress = null; pinch = twoFingers(); drawDrawDraft(scene); return; }
        }
        const t = drawTool.get();
        const p = drawPoint(e);
        if (t.mode === 'erase') {
          const hit = erasableAt(scene.drawings, boardPoint(e), meRef.current.id, meRef.current.role, 6 / scene.view.zoom);
          if (hit) actRef.current({ kind: 'draw.erase', id: hit.id });
          return;
        }
        // A stamp (issue 1034): each click puts a copy down, centred on it, in ONE event so it lands whole or not at all.
        if (t.mode === 'stamp') {
          if (!t.stamp) return;
          const at = boardPoint(e);
          const drawings = placeStamp(t.stamp, at, meRef.current.id, t.planning && meRef.current.role === 'dm', undefined, { width: scene.extent.width, height: scene.extent.height, margin: scene.objectsGrid.x.basePitch });
          actRef.current({ kind: 'draw.addMany', drawings });
          return;
        }
        if (t.mode === 'stampBox') drawTool.setBox(null);
        if (t.mode === 'text') {
          // Placed on the RELEASE (qa-runs 22): a press moves the focus to the board after its handler, so a box placed
          // and focused on the press lost the focus again, and the first keys typed went to the board.
          const at = { x: p.x, y: p.y, sx: (p.x - scene.view.x) * scene.view.zoom, sy: (p.y - scene.view.y) * scene.view.zoom };
          window.addEventListener('pointerup', () => drawTool.placeText(at), { once: true });
          return;
        }
        scene.drawPress = { points: [p.x, p.y], pointerId: e.pointerId };
        drawDrawDraft(scene);
      };
      const drawMove = (e: PointerEvent) => {
        if (e.pointerType === 'touch' && scene.touches.has(e.pointerId)) {
          scene.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (scene.touches.size >= 2 && pinch) {
            const now = twoFingers();
            const panned = pan(scene.view, now.mid.x - pinch.mid.x, now.mid.y - pinch.mid.y, scene.extent, viewport());
            settle(pinch.dist > 0 ? zoomAt(panned, now.dist / pinch.dist, now.mid, scene.extent, viewport()) : panned);
            pinch = now;
            return;
          }
        }
        const press = scene.drawPress;
        if (press && press.pointerId === e.pointerId) {
          const p = drawPoint(e);
          const t = drawTool.get();
          if (t.mode === 'stroke') {
            const n = press.points.length;
            if (Math.hypot(p.x - press.points[n - 2]!, p.y - press.points[n - 1]!) * scene.view.zoom >= 1.5) press.points.push(p.x, p.y);
          } else press.points = [press.points[0]!, press.points[1]!, p.x, p.y];
          drawDrawDraft(scene);
          return;
        }
        if (drawTool.get().mode === 'erase' && e.pointerType !== 'touch') {
          const hit = erasableAt(scene.drawings, boardPoint(e), meRef.current.id, meRef.current.role, 6 / scene.view.zoom);
          if ((hit?.id ?? null) !== scene.drawHover) { scene.drawHover = hit?.id ?? null; drawDrawDraft(scene); }
        }
      };
      const drawUp = (e: PointerEvent) => {
        scene.touches.delete(e.pointerId);
        if (scene.touches.size < 2) pinch = null;
        const press = scene.drawPress;
        if (!press || press.pointerId !== e.pointerId) return;
        scene.drawPress = null;
        const t = drawTool.get();
        if (t.mode === 'erase' || t.mode === 'text' || t.mode === 'stamp' || e.type === 'pointercancel') { drawDrawDraft(scene); return; }
        const end = drawPoint(e);
        // Save as stamp (issue 1034): the box drawn waits for its name in the toolbar; a click is no box.
        if (t.mode === 'stampBox') {
          const [x0, y0] = press.points as [number, number];
          if (Math.abs(end.x - x0) * scene.view.zoom >= 4 || Math.abs(end.y - y0) * scene.view.zoom >= 4) drawTool.setBox({ x0, y0, x1: end.x, y1: end.y });
          drawDrawDraft(scene);
          return;
        }
        let points: number[];
        if (t.mode === 'stroke') points = simplify([...press.points, end.x, end.y]);
        else {
          points = simplify([press.points[0]!, press.points[1]!, end.x, end.y], 0);
          // A click with a line or a shape is no mark.
          if (points.length < 4 || (points[0] === points[2] && points[1] === points[3])) { drawDrawDraft(scene); return; }
          points = points.slice(0, 4);
        }
        // The mark stays on this screen until the table has it, so it never blinks out between release and arrival.
        scene.drawPress = { points, pointerId: -1 };
        drawDrawDraft(scene);
        const drawing: Drawing = { id: crypto.randomUUID(), ownerId: meRef.current.id, kind: t.mode, points, colour: t.colour || scene.ownHex, width: t.width, ...(t.planning && meRef.current.role === 'dm' ? { planning: true as const } : {}) };
        actRef.current({ kind: 'draw.add', drawing }, () => { if (scene.drawPress?.pointerId === -1) scene.drawPress = null; drawDrawDraft(scene); });
      };
      /** The token under the hand (or finger) is carried along its route; the readout says what the move would spend. */
      const dragHand = (hand: Point) => {
        const d = scene.tokenDrag;
        if (!d) return;
        d.route = extendRoute(d.route, hand, scene.objectsGrid, scene.extent, d.size, scene.snap);
        const node = scene.nodes.get(d.id); const o = scene.objects[d.id];
        const at = scene.snap ? snapToCells(scene.objectsGrid, scene.extent, hand, d.size).centre : hand;
        if (node && o) layoutNode(scene, node, o, at);
        // The drag's readout (issue 1151): what the move would spend, by the table's rule and the difficult terrain held here.
        if (o && !scene.placing) {
          // The kind of move picked says its limit as the hand goes (qa-runs 1263), not only at the drop.
          const v = moveStore.get();
          const plan = v.token === d.id ? v.plan : null;
          const reach = plan && (plan.mode === 'kind' || plan.mode === 'rest') ? { label: plan.kind.label, budget: plan.budget } : null;
          dragReadout.set(dragCostLabel({ x: o.x, y: o.y }, [...d.route, at], d.size, scene.objectsGrid, scene.extent, scene.distance, heldDifficult(storeRef.current.state), reach));
        }
        for (const f of d.followers) {
          const p = followerAt(f, d.route[0]!, at, snapper());
          scene.groupAt.set(f.id, p);
          const n = scene.nodes.get(f.id); const fo = scene.objects[f.id];
          if (n && fo) layoutNode(scene, n, fo, p);
        }
      };
      /**
       * The area on this screen (qa-runs#1161): the token selected (or carried) that this person may move — its plan to
       * the picker, its reach drawn in their colours. Nothing while a move waits for Confirm, placing, or several carried.
       */
      const refreshMove = () => {
        const v = moveStore.get();
        if (v.pending) return;
        const d = scene.tokenDrag;
        const sel = [...selection.get()];
        const id = d ? (d.followers.length ? null : d.id) : sel.length === 1 ? sel[0]! : null;
        const may = id ? scene.nodes.get(id)?.mayMove === true : false;
        const r = id && may && !scene.placing && !scene.editing ? tokenArea({ state: storeRef.current.state as unknown as AreaState, encounter: scene.encounter, id, grid: scene.objectsGrid, extent: scene.extent, distance: scene.distance, reach: reachRef.current, actions: actionsRef.current, pick: v.token === id ? v.pick ?? undefined : undefined }) : null;
        moveStore.show(r ? id : null, r?.plan ?? null);
        drawArea(scene.moveLayer, r?.reach ?? null, areaColours(getLook().palette, getLook().theme), scene.view.zoom);
        scene.dirty = true;
      };
      refreshMoveRef.current = refreshMove;
      /**
       * A move of a kind (or the rest of a Move) waits for Confirm (qa-runs#1161): "Move: 25 of 30 ft". True when it
       * waits; false to send at once (free, or no speed — said with its note).
       */
      const askConfirm = (id: string, start: Point, route: Point[], rev: number, putBack: () => void): boolean => {
        const v = moveStore.get();
        const plan = v.token === id ? v.plan : null;
        // Floors (qa-runs#1160 slice B): a move ending on a stair or opening may step onto the floor it joins, and a move
        // in a scene with floors (or a token with a height) may climb or drop; the bar offers both.
        const st = storeRef.current.state;
        const t = scene.objects[id];
        const fs = (st.scene.floors ?? []) as Floor[];
        const end = route[route.length - 1] ?? start;
        // A charge is the straight line to where it ends, however the hand went (qa-runs#366).
        if (plan?.mode === 'kind' && plan.kind.charge) route = [end];
        const from = t && fs.length > 1 ? floorOf(t as { floor?: string }, fs) : null;
        const steps = from ? neighbourFloors(fs, from).filter((n) => joinsAt(fs, from, n, cellOf(scene.objectsGrid, scene.extent, end))).map((n) => ({ id: n, name: fs.find((f) => f.id === n)!.name })) : [];
        const climbs = !!t && (fs.length > 1 || typeof (t as { z?: number }).z === 'number');
        const kinded = !!plan && !!scene.distance && (plan.mode === 'kind' || plan.mode === 'rest');
        if (!kinded && !steps.length) {
          // No speed, or a 5-foot step taken this turn (slice B: no other move): said; the move goes as a plain one, the table's check the final word.
          if (plan?.mode === 'nospeed') scene.say(NO_SPEED_NOTE);
          if (plan?.mode === 'stepped') scene.say(stepTakenNote(plan.kind.label));
          return false;
        }
        const cost = kinded && t ? routeCost(start, route, t.size, scene.objectsGrid, scene.extent, scene.distance, heldDifficult(st)) : null;
        const label = kinded ? pendingLabel(plan!, cost?.squares ?? 0, scene.distance!) ?? '' : 'Move';
        const done = () => { moveStore.setPending(null); refreshMove(); };
        moveStore.setPending({
          id, label,
          ...(steps.length ? { floors: steps } : {}),
          ...(climbs && t && scene.distance ? { height: { z: elevation(t as { floor?: string; z?: number }, st.scene), perSquare: scene.distance.perSquare, unit: scene.distance.unit } } : {}),
          confirm: (extra) => { actRef.current({ ...(kinded ? confirmedMove(plan!, { id, route, rev }) : { kind: 'object.move' as const, id, route, rev }), ...(extra ?? {}) }, (reply) => { if (!reply.ok) { putBack(); if (reply.reason) scene.say(reply.reason); } }); done(); },
          cancel: () => { putBack(); done(); },
        });
        return true;
      };
      /** The hand lets go: the route commits as one move, or the token goes back. */
      const drop = (hand: Point) => {
        const d = scene.tokenDrag;
        if (!d) return;
        scene.tokenDrag = null;
        dragReadout.set(null);
        const o = scene.objects[d.id];
        const node = scene.nodes.get(d.id);
        if (node) node.cursor = 'grab';
        const full = capRoute(finishRoute(d.route, hand, scene.objectsGrid, scene.extent, d.size, scene.snap), OBJECT_LIMITS.route);
        scene.groupAt.clear();
        const putBack = () => { for (const id of [d.id, ...d.followers.map((f) => f.id)]) { const cur = scene.objects[id]; const n = scene.nodes.get(id); if (cur && n) layoutNode(scene, n, cur); } };
        if (!o || full.length < 2) { putBack(); return; }
        // Moved by somebody else while under the hand (design/21 §4.2: the same thing at once), the move is made against
        // the count picked up, so the first stands and this person is told; read at the release, the other move was
        // silently overridden. Any other change meanwhile (a number, a name) is no rival to a move: the count as it is now.
        const start = d.route[0]!;
        const madeAgainst = o.x !== start.x || o.y !== start.y ? d.rev : moveRev(o);
        if (d.followers.length) {
          // One event for the whole group (issue 830): every screen watches them set off together.
          const moves = groupMoves({ id: d.id, rev: madeAgainst }, full, d.followers, snapper(), scene.placing);
          // A wall in the way (issue 1144): refused here as the server would, and nothing is sent.
          if (wallRefuses(scene, moves)) { scene.say(WALL_IN_THE_WAY); putBack(); return; }
          actRef.current({ kind: 'objects.move', moves }, (reply) => {
            if (!reply.ok) { putBack(); return; }
            if (d.lockedStayed) scene.say(`${d.lockedStayed} locked ${d.lockedStayed === 1 ? 'thing' : 'things'} in the selection stayed where ${d.lockedStayed === 1 ? 'it is' : 'they are'}.`);
          });
          return;
        }
        // Placing the table (issue 811): the token lands on its square, with no route for anyone to watch it travel.
        const route = scene.placing ? [full[full.length - 1]!] : full;
        if (wallRefuses(scene, [{ id: d.id, route: full }])) { scene.say(WALL_IN_THE_WAY); putBack(); return; }
        // The kind of move and its area (qa-runs#1161): the drop waits for Confirm; the token stands where it was let go.
        if (!scene.placing && askConfirm(d.id, start, route, madeAgainst, putBack)) { if (node) layoutNode(scene, node, o, full[full.length - 1]); return; }
        // Commits on release; refused, the token returns to where it started.
        actRef.current({ kind: 'object.move', id: d.id, route, rev: madeAgainst }, (reply) => {
          if (!reply.ok) { const cur = scene.objects[d.id]; const n = scene.nodes.get(d.id); if (cur && n) layoutNode(scene, n, cur); }
        });
      };
      /** A drag ended by something else (a second finger, issue 1178): the token goes back where it stood, nothing sent. */
      const cancelDrag = () => {
        const d = scene.tokenDrag;
        if (!d) return;
        scene.tokenDrag = null; dragReadout.set(null); scene.groupAt.clear();
        for (const id of [d.id, ...d.followers.map((f) => f.id)]) { const cur = scene.objects[id]; const n = scene.nodes.get(id); if (n) n.cursor = 'grab'; if (cur && n) layoutNode(scene, n, cur); }
      };
      // ── Touch (issue 1178): a finger pans, two pinch, a tap selects, a long press pings or opens a token's menu, a
      // finger on your own token drags it, and tap your token then a square to move it there. Mouse and pen never come here.
      const gestures = new TouchGestures();
      let longTimer: ReturnType<typeof setTimeout> | null = null;
      const stopLong = () => { if (longTimer) { clearTimeout(longTimer); longTimer = null; } };
      let marked = false;
      const unmark = () => { if (marked) { marked = false; scene.overlay.clear(); scene.dirty = true; } };
      /** No tool has the board: the gestures are the board's own. A tool keeps its handling, a finger as a mouse. */
      const touchPlain = () => !(scene.editing || scene.pointing || scene.measuring || scene.areaMode || scene.fogging || scene.zoning || scene.drawing || scene.walling || scene.targeting || scene.explaining);
      const atClient = (x: number, y: number) => boardPoint({ clientX: x, clientY: y } as MouseEvent);
      /** The token under a finger: the one Pixi hit on its footprint, else one within a finger's reach (44 px). */
      const fingerToken = (x: number, y: number): string | null => {
        const hit = scene.touchToken ?? null; scene.touchToken = null;
        if (hit && scene.objects[hit]) return hit;
        const things = Object.values(scene.objects).filter((o) => scene.nodes.get(o.id)?.visible !== false)
          .map((o) => ({ id: o.id, fp: footprintAt(scene.objectsGrid, scene.extent, { x: o.x, y: o.y }, o.size) }));
        return tokenAtTouch(things, atClient(x, y), scene.view.zoom);
      };
      const raise = (id: string) => { scene.raised.set(id, ++scene.raiseCount); restack(scene, raiseOrder(Object.values(scene.objects), scene.raised, layerOf)); };
      /** Immediate feedback: with a token waiting, the square under the finger lights before it lifts. */
      const markDestination = (x: number, y: number) => {
        const id = tapMoveStore.get(); const o = id ? scene.objects[id] : undefined;
        const p = atClient(x, y);
        if (!o || !onBoard(p, scene.extent)) return;
        const centre = scene.snap ? snapToCells(scene.objectsGrid, scene.extent, p, o.size).centre : p;
        const fp = footprintAt(scene.objectsGrid, scene.extent, centre, o.size);
        scene.overlay.clear().rect(fp.x, fp.y, fp.width, fp.height).fill({ color: 0x38bdf8, alpha: 0.25 }).stroke({ color: 0x38bdf8, width: 2 / scene.view.zoom });
        marked = true; scene.dirty = true;
      };
      const onTap = (token: string | null, x: number, y: number) => {
        const armed = tapMoveStore.get();
        if (token) {
          const o = scene.objects[token];
          if (!o) return;
          raise(token);
          // The waiting token tapped again: the move is off; it stays selected.
          if (armed === token) { tapMoveStore.set(null); return; }
          selection.only(token);
          if (scene.nodes.get(token)?.mayMove !== true) return;
          if (o.locked) { scene.say(`${o.name ?? 'That token'} is locked.`); return; }
          tapMoveStore.set(token);
          return;
        }
        const p = atClient(x, y);
        if (armed) {
          const o = scene.objects[armed];
          // Beyond the board's edge: the move is off.
          if (!o || !onBoard(p, scene.extent)) { tapMoveStore.set(null); return; }
          const r = tapMove({ ...(o as unknown as StepThing), mine: scene.nodes.get(armed)?.mayMove === true }, p, {
            grid: scene.objectsGrid, extent: scene.extent, walls: scene.walls, isDm, movesAny: scene.movesAny === true, placing: scene.placing, encounter: scene.encounter, snap: scene.snap, routeMax: OBJECT_LIMITS.route,
          });
          if (!r) return;
          // Refused on the screen, in a drag's words; the token keeps waiting for another square.
          if ('say' in r) { scene.say(r.say); return; }
          tapMoveStore.set(null);
          // The kind of move and its area (qa-runs#1161): a tap's move waits for Confirm as a drop's does.
          const tapped = r.body as { id: string; route: Point[]; rev: number };
          const n = scene.nodes.get(armed);
          const back = () => { const cur = scene.objects[armed]; if (cur && n) layoutNode(scene, n, cur); };
          if (!scene.placing && askConfirm(armed, { x: o.x, y: o.y }, tapped.route, tapped.rev, back)) { if (n) layoutNode(scene, n, o, r.to); return; }
          actRef.current(r.body);
          return;
        }
        // Nothing waiting: what a click on the empty board does — a door's marker, else the selection clears.
        const door = doorAt(scene.doors, p, DOOR_PICK_PX / scene.view.zoom);
        if (door) { const r = doorClick(door, isDm && mayActRef.current('door.set')); if ('body' in r) actRef.current(r.body); else scene.say(r.say); return; }
        selection.clear();
      };
      const touchDo = (intents: TouchIntent[]) => {
        const r = el.getBoundingClientRect();
        for (const it of intents) {
          switch (it.kind) {
            case 'press': if (!it.token) markDestination(it.x, it.y); break;
            case 'pinchStart': stopLong(); unmark(); break;
            case 'pinch': {
              const panned = pan(scene.view, it.dx, it.dy, scene.extent, viewport());
              settle(zoomAt(panned, it.factor, { x: it.mid.x - r.left, y: it.mid.y - r.top }, scene.extent, viewport()));
              break;
            }
            case 'pan': stopLong(); unmark(); settle(pan(scene.view, it.dx, it.dy, scene.extent, viewport())); break;
            case 'dragStart': {
              stopLong(); unmark();
              const o = scene.objects[it.token]; const node = scene.nodes.get(it.token);
              if (!o || !node) break;
              raise(it.token);
              if (!selection.get().has(it.token)) selection.only(it.token);
              if (o.locked) { scene.say(`${o.name ?? 'That token'} is locked.`); break; }
              pickUp(scene, o, node);
              break;
            }
            case 'dragMove': if (scene.tokenDrag) dragHand(atClient(it.x, it.y)); break;
            case 'dragEnd': if (scene.tokenDrag) drop(atClient(it.x, it.y)); break;
            case 'dragCancel': cancelDrag(); break;
            case 'tap': stopLong(); unmark(); boardBus.hoverToken(null); onTap(it.token, it.x, it.y); break;
            case 'longPress': {
              unmark();
              // A long press is the right button's (issue 812) on a token — its menu — and the hold to ping (issue 1039) on the ground.
              if (it.token && scene.objects[it.token]) { const selected = [...selection.get()]; selection.only(it.token); boardBus.openTokenMenu({ id: it.token, x: it.x, y: it.y, selected }); boardBus.hoverToken({ id: it.token, at: { x: it.x, y: it.y - 120 }, touch: true, ...(selected[0] && selected[0] !== it.token ? { from: selected[0] } : {}) }); }
              else { const at = atClient(it.x, it.y); pingRef.current(at.x, at.y); }
              break;
            }
            case 'release': stopLong(); unmark(); break;
          }
        }
        scene.dirty = true;
      };
      const touchDown = (e: PointerEvent) => {
        const token = fingerToken(e.clientX, e.clientY);
        touchDo(gestures.down(e.pointerId, e.clientX, e.clientY, performance.now(), token, token ? scene.nodes.get(token)?.mayMove === true : false));
        stopLong();
        if (gestures.fingers === 1) longTimer = setTimeout(() => { longTimer = null; touchDo(gestures.hold(performance.now())); }, LONG_PRESS_MS);
      };
      const onDown = (e: PointerEvent) => {
        scene.dirty = true; // whatever the hand did, the next frame draws it
        if (e.button !== 0) return;
        try { el.setPointerCapture(e.pointerId); } catch { /* a pointer the browser no longer tracks: the press still counts */ }
        el.focus({ preventScroll: true });
        // A finger on the board with no tool (issue 1178), or any finger while a gesture runs: the gestures have it.
        if (e.pointerType === 'touch' && (gestures.fingers > 0 || (touchPlain() && !scene.pressed && !scene.dragging))) { touchDown(e); return; }
        scene.touchToken = null;
        if (scene.dragging || scene.tokenDrag || scene.pressed) { scene.pressed = false; return; } // a line or a thing took the press (their handlers run first)
        // Drawing (issue 1032): one finger draws; a second one turns the gesture into a pan and zoom.
        if (scene.drawing) { drawDown(e); return; }
        // Walls (issue 1144): with no chain begun, a press on a wall picks it; otherwise each press is a corner.
        if (scene.walling) {
          const p = boardPoint(e);
          if (wallTool.get().lights) { wallTool.lightClick(p, scene.lights, LIGHT_PICK_PX / scene.view.zoom); return; }
          const hit = wallTool.get().chain.length ? null : wallAt(scene.walls, p, WALL_PICK_PX / scene.view.zoom);
          if (hit) wallTool.select(hit.id); else wallTool.corner(wallCorner(scene.objectsGrid, scene.extent, p, e.shiftKey));
          return;
        }
        // Fog (issue 851): the press starts a box of squares to reveal or cover; the release sends it.
        if (scene.fogging) { fogTool.press(cellUnder(e)); return; }
        // Zones (issue 1149): the same, a box painted into a zone or erased.
        if (scene.zoning) { zoneTool.press(cellUnder(e)); return; }
        // Openings (qa-runs#1160 slice B): the DM's press marks or clears a square of the viewed floor as open onto the floor below.
        if (isDm && floorView.marking()) { floorView.mark(cellUnder(e)); return; }
        if (scene.pointing) { const c = cellUnder(e); scene.focusDrag = { from: c, to: c, moved: false }; drawFocuses(scene, colourRef.current); return; }
        // Measuring (issue 832): the drag is the ruler. Laying an area: the press places its point of origin.
        if (scene.measuring) { const c = cellUnder(e); const p = boardPoint(e); scene.measureDrag = true; measureTool.span(c, c, p, p); return; }
        // An area left ungridded (issue 1035): the drag is its rectangle, sent as one grid.set on release.
        if (scene.editing && gridEditor.get().drawingBlank) { const p = boardPoint(e); scene.blankBox = { from: p, to: p }; return; }
        if (scene.areaMode) { areaTool.place(nearestIntersection(scene.objectsGrid, scene.extent, boardPoint(e))); return; }
        // Picking a target (issue 1130): a tap on the ground is no one's — it neither selects, drags nor pans.
        if (scene.targeting) return;
        // A door (issue 1148): outside every tool its marker is clicked as a prop is — the DM's click opens or closes
        // it; a player's sends nothing and says to ask the DM.
        if (!scene.editing) {
          const door = doorAt(scene.doors, boardPoint(e), DOOR_PICK_PX / scene.view.zoom);
          if (door) {
            const r = doorClick(door, isDm && mayActRef.current('door.set'));
            if ('body' in r) actRef.current(r.body); else scene.say(r.say);
            return;
          }
        }
        if (e.shiftKey && isDm && !scene.editing) { const p = boardPoint(e); scene.box = { from: p, to: p }; return; }
        if (!e.shiftKey) selection.clear();
        drag = { x: e.clientX, y: e.clientY };
        letGo();
        const at = boardPoint(e);
        hold = { x: e.clientX, y: e.clientY, timer: setTimeout(() => { hold = null; drag = null; pingRef.current(at.x, at.y); }, PING_HOLD_MS) };
      };
      const onMove = (e: PointerEvent) => {
        scene.dirty = true; // whatever the hand did, the next frame draws it
        if (e.pointerType === 'touch' && gestures.has(e.pointerId)) { touchDo(gestures.move(e.pointerId, e.clientX, e.clientY)); return; }
        // A drag of something that is not yours (issue 795): nothing moves, and now it says why — once the
        // hand has travelled, so a click to read a token's properties stays quiet.
        const nm = scene.notMine;
        if (nm && !nm.said && e.buttons !== 0 && Math.hypot(e.clientX - nm.x, e.clientY - nm.y) > NOT_MINE_SLOP_PX) {
          nm.said = true;
          scene.say(notYours(nm.name));
        }
        if (scene.fogging && fogTool.get().box) { fogTool.drag(cellUnder(e)); return; }
        if (scene.zoning && zoneTool.get().box) { zoneTool.drag(cellUnder(e)); return; }
        if (scene.walling) { if (wallTool.get().chain.length) wallTool.hover(wallCorner(scene.objectsGrid, scene.extent, boardPoint(e), e.shiftKey)); return; }
        if (scene.drawing) { drawMove(e); return; }
        if (scene.measureDrag) {
          const r = measureTool.get(); const c = cellUnder(e); const p = boardPoint(e);
          if (r.from && (isGridless(scene.objectsGrid) || c.i !== r.to?.i || c.j !== r.to?.j)) measureTool.span(r.from, c, r.fromAt ?? p, p);
          return;
        }
        if (scene.blankBox) {
          scene.blankBox.to = boardPoint(e);
          const b = scene.blankBox; const x = Math.min(b.from.x, b.to.x); const y = Math.min(b.from.y, b.to.y);
          scene.overlay.clear().rect(x, y, Math.abs(b.to.x - b.from.x), Math.abs(b.to.y - b.from.y)).fill({ color: 0xf59e0b, alpha: 0.15 }).stroke({ color: 0xf59e0b, width: 1 / scene.view.zoom });
          return;
        }
        if (scene.areaMode && !areaTool.get().origin) {
          const c = nearestIntersection(scene.objectsGrid, scene.extent, boardPoint(e));
          if (c.i !== scene.areaHover?.i || c.j !== scene.areaHover?.j) { scene.areaHover = c; drawAreas(scene, colourRef.current); }
        }
        if (scene.focusDrag) {
          const c = cellUnder(e);
          if (c.i !== scene.focusDrag.to.i || c.j !== scene.focusDrag.to.j) { scene.focusDrag.to = c; scene.focusDrag.moved = true; drawFocuses(scene, colourRef.current); }
          return;
        }
        if (!scene.dragging && !scene.tokenDrag && !scene.box && !drag) {
          // The focus under the hand, for its note (decision 33).
          const c = cellUnder(e);
          const over = Object.values(scene.focuses).find((f) => f.cells.some((x) => x.i === c.i && x.j === c.j)) ?? null;
          const r = el.getBoundingClientRect();
          if (over?.id !== scene.hovered) { scene.hovered = over?.id ?? null; drawFocuses(scene, colourRef.current); }
          boardBus.hoverFocus(over ? { id: over.id, at: { x: e.clientX - r.left, y: e.clientY - r.top } } : null);
        }
        if (scene.dragging && scene.gridSource) {
          const p = boardPoint(e);
          const { axis, index } = scene.dragging;
          const along = axis === 'x' ? scene.extent.width : scene.extent.height;
          const pos = Math.max(0, Math.min(along, fromBoard(scene.gridSource[axis].edge, axis === 'x' ? p.x : p.y, along)));
          gridEditor.draft({ ...scene.gridSource, [axis]: dragLine(scene.gridSource[axis], index, pos) });
          return;
        }
        if (scene.tokenDrag) { dragHand(boardPoint(e)); return; }
        if (scene.box) {
          scene.box.to = boardPoint(e);
          const b = scene.box; const x = Math.min(b.from.x, b.to.x); const y = Math.min(b.from.y, b.to.y);
          scene.overlay.clear().rect(x, y, Math.abs(b.to.x - b.from.x), Math.abs(b.to.y - b.from.y)).fill({ color: 0xfbbf24, alpha: 0.12 }).stroke({ color: 0xfbbf24, width: 1 / scene.view.zoom });
          return;
        }
        if (hold && Math.hypot(e.clientX - hold.x, e.clientY - hold.y) > PING_SLOP_PX) letGo();
        if (!drag) return;
        const dx = e.clientX - drag.x; const dy = e.clientY - drag.y;
        drag = { x: e.clientX, y: e.clientY };
        settle(pan(scene.view, dx, dy, scene.extent, viewport()));
      };
      const onUp = (e: PointerEvent) => {
        scene.dirty = true; // whatever the hand did, the next frame draws it
        if (e.pointerType === 'touch' && gestures.has(e.pointerId)) { touchDo(gestures.up(e.pointerId, e.clientX, e.clientY, e.type === 'pointercancel')); return; }
        drag = null;
        letGo();
        scene.measureDrag = false; // the ruler stays until the next drag, or Escape
        scene.notMine = null;
        if (scene.fogging && fogTool.get().box) { fogTool.release(cellUnder(e)); return; }
        if (scene.zoning && zoneTool.get().box) { zoneTool.release(cellUnder(e), scene.zones); return; }
        if (scene.walling) return;
        if (scene.drawing || scene.touches.size) { drawUp(e); return; }
        if (scene.focusDrag) {
          const d = scene.focusDrag;
          scene.focusDrag = null;
          const to = cellUnder(e);
          if (d.moved || to.i !== d.from.i || to.j !== d.from.j) focusTool.addBox(d.from, to); else focusTool.toggleCell(d.from);
          drawFocuses(scene, colourRef.current);
          return;
        }
        if (scene.dragging) {
          scene.dragging = null;
          const draft = gridEditor.get().draft;
          if (draft) commitRef.current?.(draft); // one event on release: players see the finished grid
        }
        if (scene.tokenDrag) drop(boardPoint(e));
        if (scene.blankBox) {
          const b = scene.blankBox; scene.blankBox = null; scene.overlay.clear();
          const area = blankArea(b.from, b.to, scene.extent);
          gridEditor.drawBlank(false);
          const g = scene.gridSource;
          if (area && g) commitRef.current?.({ ...g, blank: [...(g.blank ?? []), area].slice(-GRID_LIMITS.maxBlank) });
          return;
        }
        if (scene.box) {
          const b = scene.box; scene.box = null; scene.overlay.clear();
          const x0 = Math.min(b.from.x, b.to.x); const x1 = Math.max(b.from.x, b.to.x); const y0 = Math.min(b.from.y, b.to.y); const y1 = Math.max(b.from.y, b.to.y);
          const hit = Object.values(scene.objects).filter((o) => {
            const fp = footprintAt(scene.objectsGrid, scene.extent, { x: o.x, y: o.y }, o.size);
            return fp.x < x1 && fp.x + fp.width > x0 && fp.y < y1 && fp.y + fp.height > y0;
          }).map((o) => o.id);
          selection.replace(hit);
        }
      };
      const onWheel = (e: WheelEvent) => {
        scene.dirty = true; // whatever the hand did, the next frame draws it
        e.preventDefault();
        const r = el.getBoundingClientRect();
        settle(zoomAt(scene.view, wheelFactor(e.deltaY, e.deltaMode), { x: e.clientX - r.left, y: e.clientY - r.top }, scene.extent, viewport()));
      };
      // Keyboard stepping: one square a press, each press a move of its own (design/12 §4). Delete asks the confirmation.
      const onKey = (e: KeyboardEvent) => {
        scene.dirty = true; // whatever the hand did, the next frame draws it
        if (e.key === 'Escape' && scene.pointing) { e.preventDefault(); focusTool.stop(); return; }
        if (e.key === 'Escape' && scene.targeting) { e.preventDefault(); targetTool.stop(); return; }
        if (e.key === 'Escape' && scene.explaining) { e.preventDefault(); whatsThisTool.stop(); return; }
        if (e.key === 'Escape' && fogTool.get().on) { e.preventDefault(); fogTool.stop(); return; }
        if (e.key === 'Escape' && zoneTool.get().on) { e.preventDefault(); zoneTool.stop(); return; }
        if (e.key === 'Escape' && (scene.measuring || scene.areaMode)) { e.preventDefault(); measureTool.stop(); areaTool.stop(); return; }
        if (typingIn(e.target)) return; // a key typed into a box over the board is the box's (issue 1422)
        const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1], a: [-1, 0], d: [1, 0], w: [0, -1], s: [0, 1] };
        const ids = [...selection.get()];
        // The token's menu from the keyboard (issue 812): the Menu key, or Shift+F10, opens it at the one selected thing.
        if ((e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) && ids.length === 1) {
          const o = scene.objects[ids[0]!];
          if (o) {
            e.preventDefault();
            const r = el.getBoundingClientRect();
            boardBus.openTokenMenu({ id: o.id, x: r.left + (o.x - scene.view.x) * scene.view.zoom, y: r.top + (o.y - scene.view.y) * scene.view.zoom });
          }
          return;
        }
        if ((e.key === 'Delete' || e.key === 'Backspace') && mayActRef.current('object.remove') && ids.length) { e.preventDefault(); boardBus.requestDelete(ids); return; }
        // Reveal from the board itself, mid-sentence (design/11 §2): R reveals what is selected, H hides it.
        if ((e.key === 'r' || e.key === 'h') && isDm && mayActRef.current('object.reveal') && ids.length && !e.ctrlKey && !e.metaKey) { e.preventDefault(); boardBus.requestReveal(ids, e.key === 'r'); return; }
        const step = steps[e.key];
        if (step && isDm && mayActRef.current('objects.move') && ids.length > 1) {
          // The arrow keys step a whole selection, one square, as one event (issue 830).
          e.preventDefault();
          const things = steppable(scene.objects as unknown as Record<string, GroupThing>, ids);
          if (!things.length) return;
          const moves = things.map((t) => {
            const to = stepCell(scene.objectsGrid, scene.extent, { x: t.x, y: t.y }, t.size, step[0], step[1]);
            return { id: t.id, route: scene.placing ? [to] : [{ x: t.x, y: t.y }, to], rev: t.rev };
          });
          if (wallRefuses(scene, moves)) { scene.say(WALL_IN_THE_WAY); return; }
          actRef.current({ kind: 'objects.move', moves });
          return;
        }
        if (!step || ids.length !== 1) return;
        const o = scene.objects[ids[0]!];
        if (!o || o.locked) return;
        e.preventDefault();
        // The one rule the Tokens list shares (issue 1175): yours to move, the turn (design/20 §4.1), the walls held.
        const r = keyStep(o as unknown as StepThing, step[0], step[1], {
          grid: scene.objectsGrid, extent: scene.extent, walls: scene.walls, isDm, movesAny: scene.movesAny === true, placing: scene.placing, encounter: scene.encounter,
        });
        if (!r) return;
        if ('say' in r) { scene.say(r.say); return; }
        actRef.current(r.body);
      };
      el.addEventListener('pointerdown', onDown);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      // A press the browser took back ends with nothing sent (the review of 2026-10-10): never the release's commit.
      const onCancel = (e: PointerEvent) => {
        scene.dirty = true;
        if (e.pointerType === 'touch' && gestures.has(e.pointerId)) { touchDo(gestures.up(e.pointerId, e.clientX, e.clientY, true)); return; }
        drag = null;
        letGo();
        if (scene.drawing || scene.touches.size) drawUp(e); // drawUp keeps nothing of a cancelled stroke
        cancelPress(scene, { cancelDrag, redrawFocuses: () => drawFocuses(scene, colourRef.current) });
      };
      el.addEventListener('pointercancel', onCancel);
      el.addEventListener('wheel', onWheel, { passive: false });
      el.addEventListener('keydown', onKey);
      // The browser's own menu never opens over the board: the right button is the token's menu (issue 812).
      const onContext = (e: MouseEvent) => e.preventDefault();
      el.addEventListener('contextmenu', onContext);
      // Nor does the right button's press take the focus: the board is focusable, and the press's default
      // action moved focus off the menu it had just opened (found on vtt-dev).
      const onRightPress = (e: MouseEvent) => { if (e.button === 2) e.preventDefault(); };
      el.addEventListener('mousedown', onRightPress);
      const offFit = boardBus.onFit(() => settle(fitToScreen(scene.extent, viewport())));
      // Travel: the view centres on a point at the current zoom — a stone's creature, a focus's arrow. Only this person's view moves.
      const centreOn = (p: Point) => { const vp = viewport(); settle(clampView({ x: p.x - vp.width / 2 / scene.view.zoom, y: p.y - vp.height / 2 / scene.view.zoom, zoom: scene.view.zoom }, scene.extent, vp)); };
      const offFocus = boardBus.onFocus((id) => { const o = scene.objects[id]; if (!o) return; centreOn(o); selection.only(id); });
      const offGoTo = boardBus.onGoTo(centreOn);
      const offPointing = focusTool.subscribe((d) => {
        scene.pointing = d.on;
        if (!d.on) scene.focusDrag = null;
        drawFocuses(scene, colourRef.current);
      });
      const offMeasure = measureTool.subscribe((r) => {
        scene.measuring = r.on;
        drawRuler(scene);
      });
      const offArea = areaTool.subscribe((d) => {
        scene.areaMode = d.on;
        if (!d.on) scene.areaHover = null;
        drawAreas(scene, colourRef.current);
      });
      const offTarget = targetTool.subscribe((t) => {
        scene.targeting = t.on;
        if (t.on) boardBus.hoverToken(null);
      });
      const offExplain = whatsThisTool.subscribe((t) => {
        scene.explaining = t.on;
        if (t.on) boardBus.hoverToken(null);
      });
      const offDraw = drawTool.subscribe((d) => {
        scene.drawing = d.on;
        if (!d.on) { scene.drawPress = null; scene.drawHover = null; scene.touches.clear(); }
        if (d.mode !== 'erase') scene.drawHover = null;
        drawDrawDraft(scene);
      });
      const offWall = wallTool.subscribe((d) => {
        scene.walling = d.on && isDm;
        drawWalls(scene);
      });
      // A double-click ends the chain (issue 1144); its two presses were corners at one spot, which counts once.
      const onDouble = () => { if (scene.walling) wallTool.finish(); };
      el.addEventListener('dblclick', onDouble);
      const offFog = fogTool.subscribe((d) => {
        scene.fogging = d.on && isDm;
        drawFog(scene);
      });
      const offZone = zoneTool.subscribe((d) => {
        scene.zoning = d.on && isDm;
        drawFog(scene);
      });
      // The modes as they are now, not as last heard (the retest of issue 1371): a mode that ended while the board was
      // being rebuilt (View as ends drawing) had no listener to hear it, so the new board starts from what is on.
      scene.pointing = focusTool.get().on; scene.measuring = measureTool.get().on; scene.areaMode = areaTool.get().on;
      scene.targeting = targetTool.get().on; scene.explaining = whatsThisTool.get().on; scene.drawing = drawTool.get().on;
      scene.walling = wallTool.get().on && isDm; scene.fogging = fogTool.get().on && isDm; scene.zoning = zoneTool.get().on && isDm;
      // The cursor is the host's, which outlives this build: one place sets it from the modes, and the cleanup resets it.
      const offCursor = bindBoardCursor(el, isDm);
      // Named, so the cleanup takes it off again: an anonymous one stayed on the element through every rebuild (the review of 2026-10-10).
      const onLeave = () => { if (scene.hovered) { scene.hovered = null; drawFocuses(scene, colourRef.current); } boardBus.hoverFocus(null); };
      el.addEventListener('pointerleave', onLeave);
      const offEditor = gridEditor.subscribe((s) => {
        scene.editing = s.setupOn && isDm;
        scene.gridSource = s.draft ?? gridRef.current;
        drawGrid(scene, scene.gridSource, isDm, s.draft !== null);
      });
      const offSelection = selection.subscribe(relayout);
      const offMoveSel = selection.subscribe(refreshMove);
      const offMoveStore = moveStore.subscribe(refreshMove);
      // Tap to move: the waiting token is rung (a relayout), and leaves the wait when it leaves the selection.
      const offTapMove = tapMoveStore.subscribe(() => { relayout(); scene.dirty = true; });
      const offTapSel = selection.subscribe((ids) => { const id = tapMoveStore.get(); if (id && !ids.has(id)) tapMoveStore.set(null); });
      // A board mode turned on has the next tap: a token waiting for its square is called off, ring and hint with it.
      const offTapModes = callOffTapMoveOnModes([focusTool, measureTool, areaTool, targetTool, whatsThisTool, drawTool, wallTool, fogTool, zoneTool]);
      const offPing = boardBus.onPinged((p) => { scene.pings.push({ x: p.x, y: p.y, colour: scene.focusColours[p.by] ?? 0xfbbf24, started: performance.now() }); scene.dirty = true; });
      const offFx = effectsBus.onPlay((list) => {
        const now = performance.now();
        for (const fx of list) scene.effects.push(startPlaying(fx, now, scene.objectsGrid, scene.extent));
        effectsBus.setActive(scene.effects.length); scene.dirty = true;
      });
      // Moves play out: every frame, a travelling node is placed along its route. Then the frame is drawn — only when
      // something changed, or at the latest once a second as a safety net for a change no path marked.
      scene.app.ticker.remove(scene.app.render, scene.app);
      let drawnAt = performance.now();
      // Animated tokens (issue 1176): stepped at most ANIMATION_FPS a second, and only those on this screen now.
      const animation = new AnimationLoop<Texture>();
      let animateOn = animateSetting.get();
      const offAnimate = animateSetting.subscribe((on) => { animateOn = on; if (!on) animation.stop(); scene.dirty = true; });
      const onScreen = (): Set<Moving<Texture>> => {
        const out = new Set<Moving<Texture>>();
        animationStats.tokens = 0;
        if (!animateOn || !movingByAsset.size) return out;
        const w = scene.app.screen.width; const h = scene.app.screen.height;
        for (const node of scene.nodes.values()) {
          const m = node.asset ? movingByAsset.get(node.asset) : undefined;
          if (!m || node.destroyed || !node.visible || !node.parent?.visible) continue;
          const b = node.getBounds();
          if (b.maxX < 0 || b.maxY < 0 || b.minX > w || b.minY > h) continue;
          animationStats.tokens += 1;
          out.add(m);
        }
        return out;
      };
      const tick = () => {
        const now = performance.now();
        for (const [id, t] of scene.travels) {
          const node = scene.nodes.get(id); const o = scene.objects[id];
          const k = Math.min(1, (now - t.started) / t.ms);
          if (node && o) layoutNode(scene, node, o, pointAlong(t.route, k));
          if (k >= 1) scene.travels.delete(id);
        }
        if (scene.pings.length) drawPings(scene, now);
        // The last frame of an effect clears its layer and marks one more draw; after that the board idles again.
        if (scene.effects.length) { scene.effects = drawEffects(scene.fxLayer, scene.effects, now, scene.view.zoom); effectsBus.setActive(scene.effects.length); scene.dirty = true; }
        animation.frame(now, onScreen(), animateOn);
        if (animationChanged) { animationChanged = false; scene.dirty = true; }
        // A big map's tiles for where the board is now (issue 1172): a no-op unless the view moved or a tile arrived.
        if (scene.pyramid.active) {
          const z = scene.view.zoom;
          scene.pyramid.update({ x: scene.view.x, y: scene.view.y, width: scene.app.screen.width / z, height: scene.app.screen.height / z }, z * (scene.app.renderer.resolution || 1));
        }
        if (!scene.dirty && now - drawnAt < IDLE_REDRAW_MS) return;
        resolvePlates(scene);
        scene.dirty = false; drawnAt = now;
        scene.app.render();
      };
      scene.app.ticker.add(tick);
      // A screen turned into or out of the phone layout (issue 1242) fits the map again: the board changed shape under it.
      // The bars come and go over the next few frames, each a resize of its own, so each refits until the board settles
      // (a fit at the first size left the map in a corner of the phone's board).
      let phoneAt = phoneRef.current;
      let refitUntil = 0;
      const onResize = () => {
        if (el.clientWidth <= 0 || el.clientHeight <= 0) return;
        if (unsized) { unsized = false; phoneAt = phoneRef.current; first(); return; }
        if (phoneAt !== phoneRef.current) { phoneAt = phoneRef.current; refitUntil = performance.now() + PHONE_REFIT_MS; }
        settle(performance.now() < refitUntil ? fitToScreen(scene.extent, viewport()) : clampView(scene.view, scene.extent, viewport()));
      };
      drawFocuses(scene, colourRef.current);
      scene.app.renderer.on('resize', onResize);
      // Pixi's resizeTo listens to the WINDOW only. The host also changes size without the window: the tool strip above
      // the board opening and closing (issue 853), a dock panel dragged wider. The view is left where it was (clamped by
      // onResize), so the map's top-left stays put under the strip and the extra or lost height shows more or less below.
      // A board lent out of its panel while a layout is rebuilt (qa-runs 1199) has no size for a moment: nothing is resized
      // then, so the view is never clamped to an empty screen.
      const hostSize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => { if (el.clientWidth > 0 && el.clientHeight > 0) scene.app.queueResize(); });
      hostSize?.observe(el);
      scene.destroyed = false;
      (scene as Scene & { cleanup?: () => void }).cleanup = () => {
        el.removeEventListener('pointerdown', onDown); el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp); el.removeEventListener('pointercancel', onCancel); el.removeEventListener('pointerleave', onLeave); el.removeEventListener('wheel', onWheel);
        el.removeEventListener('keydown', onKey); el.removeEventListener('dblclick', onDouble); el.removeEventListener('contextmenu', onContext); el.removeEventListener('mousedown', onRightPress);
        hostSize?.disconnect();
        offFit(); offFocus(); offGoTo(); offPointing(); offEditor(); offSelection(); offMoveSel(); offMoveStore(); moveStore.reset(); offTapMove(); offTapSel(); offTapModes(); tapMoveStore.set(null); stopLong(); gestures.reset(); offMeasure(); offArea(); offTarget(); offExplain(); offFog(); offZone(); offWall(); offDraw(); offCursor(); offPing(); offFx(); effectsBus.setActive(0); offAnimate(); animation.stop(); animationStats.tokens = 0; letGo();
      };
    })();

    return () => {
      live = false;
      (scene as Scene & { cleanup?: () => void }).cleanup?.();
      // A view still waiting its save is saved now (qa-runs 1199), so the next board starts where this one was left.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current); saveTimer.current = null;
        if (!sameView(saved.current, scene.view)) { saved.current = scene.view; void saveView(campaignId, scene.view); }
      }
      scene.destroyed = true;
      sceneRef.current = null;
      late.current.gone();
      commitRef.current = null;
      setMapTexture(scene, Texture.EMPTY);
      scene.pyramid.destroy();
      for (const { sprite } of scene.tileSprites.values()) sprite.texture.destroy(true);
      scene.tileSprites.clear();
      if (scene.tilesOf) releaseTilesExcept(null); // the board's tile copies go with it
      scene.tilesOf = null;
      if (host.current) delete host.current.dataset.map;
      if (scene.app.renderer) scene.app.destroy(true, { children: true });
    };
  }, [campaignId, isDm, meId]);

  // The map: a picture from the library at its natural size, fetched with the bearer; none is the empty board.
  useEffect(() => {
    let live = true;
    const paint = async (scene: Scene, m: MapInfo | null) => {
      scene.extent = boardExtent(m);
      drawFloor(scene.floor, scene.extent, m !== null);
      scene.emptyFloor = m === null;
      drawFog(scene);
      drawGrid(scene, scene.gridSource ?? gridRef.current, isDm);
      scene.dirty = true;
      // `data-map` says which map is painted (issue 1121): a test can tell a lost map from a slow one.
      if (host.current && !(tilesOn && m)) delete host.current.dataset.map; // in tiles, the tiles' effect says when they are painted
      // A big map (issue 1172) whole for this viewer is drawn from its pyramid; anything else lets the pyramid go.
      scene.pyramid.set(m && !tilesOn && isTiledMap(m) ? m : null);
      if (!m) { setMapTexture(scene, Texture.EMPTY); scene.map.visible = false; return; }
      // A player's map in tiles is drawn by the tiles' own effect below; the whole picture is not fetched at all.
      if (tilesOn) { setMapTexture(scene, Texture.EMPTY); scene.map.visible = false; return; }
      if (scene.pyramid.active) { setMapTexture(scene, Texture.EMPTY); scene.map.visible = false; scene.dirty = true; if (scene.pyramid.basePainted === m.id && host.current) host.current.dataset.map = m.id; return; }
      const url = await pictureUrl(m.id, undefined, mapV);
      if (!live || scene.destroyed) return;
      if (!url) { scene.map.visible = false; scene.dirty = true; return; }
      const img = new Image();
      img.src = url;
      await img.decode().catch(() => undefined);
      if (!live || scene.destroyed) return;
      // Kept out of Pixi's global cache and the one it replaces destroyed: every map version (a player's masked map
      // changes with every fog stroke) was cached for good, a whole map's texture each — 64 MB a stroke at 4000 × 4000.
      setMapTexture(scene, Texture.from(img, true));
      scene.map.width = m.width; scene.map.height = m.height; // as uploaded: the size is the picture's own
      scene.map.visible = true;
      scene.dirty = true;
      if (host.current) host.current.dataset.map = m.id;
    };
    const tick = () => {
      const scene = sceneRef.current;
      if (!scene) { if (live) setTimeout(tick, 50); return; }
      void paint(scene, map).then(() => {
        if (live && scene && !scene.destroyed) {
          scene.view = clampView(scene.view, scene.extent, { width: host.current?.clientWidth ?? 0, height: host.current?.clientHeight ?? 0 });
          applyView(scene);
          drawThings(scene, floorThings(storeRef.current.state, meId, isDm), isDm, meId);
          drawFocuses(scene, colourRef.current);
          drawAreas(scene, colourRef.current);
          drawFog(scene);
        }
      });
    };
    tick();
    return () => { live = false; };
    // The board is rebuilt whole when the viewer changes — View as… and back (issue 1121) — so the map is painted again
    // onto the new one: the same keys as the board's own effect, beside the map's.
  }, [map?.id, map?.width, map?.height, mapV, tilesOn, campaignId, isDm, meId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A player's map in tiles (issue 1144, slice 3; design/30 §5.3): black under the whole board, and each tile they may
  // see something of a sprite at its grid-cut rectangle, asked at its hash. A tile whose hash is unchanged is left as
  // it is; a changed one is asked again and replaces its texture once it arrives; one no longer named goes, and its
  // copy with it. A missing tile stays black. The DM's map is whole (the effect above).
  useEffect(() => {
    let live = true;
    const drop = (sprite: Sprite) => { sprite.texture.destroy(true); sprite.destroy(); };
    const apply = async (scene: Scene) => {
      const want = map && tiles ? tiles : null;
      scene.tileLayer.visible = want !== null;
      scene.dirty = true;
      // Tiles of one map at a time: a map switch lets every tile of the map before go, its sprites and its copies.
      if (scene.tilesOf !== (want && map ? map.id : null)) {
        for (const { sprite } of scene.tileSprites.values()) drop(sprite);
        scene.tileSprites.clear();
        releaseTilesExcept(want && map ? map.id : null);
        scene.tilesOf = want && map ? map.id : null;
      }
      if (!want || !map) {
        scene.tileBack.clear();
        if (map) releaseTiles(map.id);
        return;
      }
      const extent = boardExtent(map);
      const layout = tileLayout(store.state.grid, extent);
      scene.tileBack.clear().rect(0, 0, extent.width, extent.height).fill({ color: 0x000000 });
      for (const [key, had] of [...scene.tileSprites]) if (!want[key]) { drop(had.sprite); scene.tileSprites.delete(key); }
      releaseTiles(map.id, new Set(Object.keys(want)));
      await Promise.all(Object.entries(want).map(async ([key, hash]) => {
        const [ti, tj] = key.split(',').map(Number) as [number, number];
        const box = tileBox(layout, ti, tj);
        const place = (sp: Sprite) => {
          sp.visible = box !== null;
          if (box) { sp.position.set(box.x0, box.y0); sp.width = box.x1 - box.x0; sp.height = box.y1 - box.y0; }
        };
        const had = scene.tileSprites.get(key);
        if (had && had.hash === hash) { place(had.sprite); return; }
        const url = await tileUrl(map.id, key, hash);
        if (!live || scene.destroyed || !url) return;
        const img = new Image();
        img.src = url;
        await img.decode().catch(() => undefined);
        if (!live || scene.destroyed) return;
        const texture = Texture.from(img, true); // kept out of Pixi's cache: each tile's copy is its own, destroyed when replaced
        const now = scene.tileSprites.get(key);
        if (now) { const old = now.sprite.texture; now.sprite.texture = texture; now.hash = hash; old.destroy(true); place(now.sprite); }
        else { const sp = new Sprite(texture); scene.tileLayer.addChild(sp); scene.tileSprites.set(key, { hash, sprite: sp }); place(sp); }
        scene.dirty = true;
      }));
      if (live && host.current) host.current.dataset.map = map.id;
    };
    const tick = () => {
      const scene = sceneRef.current;
      if (!scene) { if (live) setTimeout(tick, 50); return; }
      void apply(scene);
    };
    tick();
    return () => { live = false; };
  }, [map?.id, map?.width, map?.height, tilesKey, store.state.grid, campaignId, isDm, meId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The grid from the store (an accepted event, ours or the DM's), unless a draft is mid-drag; the things sit on it.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.objectsGrid = store.state.grid;
    if (gridEditor.get().draft) return;
    scene.gridSource = store.state.grid;
    drawGrid(scene, scene.gridSource, isDm);
    for (const [id, node] of scene.nodes) { const o = scene.objects[id]; if (o) layoutNode(scene, node, o, nodeCentre(scene, id) ?? undefined); }
    drawFocuses(scene, colourRef.current);
    drawAreas(scene, colourRef.current);
    drawRuler(scene);
    drawFog(scene);
  }, [store.state.grid, isDm]);

  // Walls (issue 1144): the DM's store holds them; a player's holds none.
  useEffect(() => {
    // A player's store holds design/30 §4's walls (ends only, issue 1144 slice 2). Their light and shadow (issue 1145) is
    // drawn per square from the sight the server sent (`sightPaint`: the server's line rule already cut each light by
    // those walls), so the canvas draws and tests only the DM's whole walls; a player learns of a wall by the refusal.
    // Door markers (issue 1148) are drawn for both.
    const walls = isDm ? floorWalls(store.state, meId, isDm) : [];
    const lights = isDm ? (store.state.lights ?? []) as PlacedLight[] : [];
    // Doors (issue 1148): every role's store's, a player's as last seen.
    // Floors (qa-runs#1160): each floor's doors on that floor alone — a player's walls carry their floor too, so a player
    // with tokens on two floors sees each floor's on its own, never merged.
    const doors = doorMarks(floorWalls(store.state, meId, isDm) as Array<Pick<Wall, 'a' | 'b' | 'door'> & { id?: string }>);
    const lightsHere = isDm ? lights.filter((l) => !store.state.scene.floors || store.state.scene.floors.length < 2 || (l.floor ?? groundFloor(store.state.scene.floors)) === viewedFloor({ scene: store.state.scene, objects: store.state.objects as Record<string, { floor?: string }> }, meId, isDm)) : lights;
    const openings = openingRows(store.state.scene.floors, viewedFloor({ scene: store.state.scene, objects: store.state.objects as Record<string, { floor?: string }> }, meId, isDm));
    const apply = (s: Scene) => { s.walls = walls; s.doors = doors; s.lights = lightsHere; s.openings = openings; drawWalls(s); drawFog(s); };
    late.current.run('walls', apply);
    return undefined;
  }, [store.state.walls, store.state.lights, store.state.scene, isDm, pickedFloor]);

  // Vision zones (issue 1149): the DM's store holds them, drawn on the fog layer; a player's holds none.
  useEffect(() => {
    const zones = isDm ? (store.state.zones ?? []) as Zone[] : [];
    const apply = (s: Scene) => { s.zones = zones; s.difficult = difficultTest(store.state); drawFog(s); };
    late.current.run('zones', apply);
    return undefined;
  }, [store.state.zones, isDm]);

  // The fog (issue 851): the DM's shade, view-as's black, a player's nothing — their map is already masked.
  useEffect(() => {
    const fog = store.state.fog ?? null;
    const style = fogStyle(isDm ? 'dm' : 'player', viewingAs !== null);
    const floors = store.state.scene.floors;
    const apply = (s: Scene) => { s.fog = fog; s.fogStyle = style; s.floors = floors; s.fogFloor = fogFloorNow; s.difficult = difficultTest(store.state); drawFog(s); };
    late.current.run('fog', apply);
    return undefined;
  }, [store.state.fog, isDm, viewingAs, fogFloorNow, store.state.scene.floors]);

  useEffect(() => {
    wordsRef.current = words;
    if (sceneRef.current) sceneRef.current.words = words;
  }, [words]);

  useEffect(() => {
    healthRef.current = health;
    if (sceneRef.current) { sceneRef.current.health = health; sceneRef.current.dirty = true; }
  }, [health]);

  useEffect(() => {
    woundsRef.current = woundRules;
    if (sceneRef.current) { sceneRef.current.wounds = woundRules; sceneRef.current.dirty = true; }
  }, [woundRules]);

  // The system's distance rule (issue 1303), for the ruler and the draft area.
  const distance = systemRules.distance;
  useEffect(() => {
    distanceRef.current = distance ?? null;
    const apply = (s: Scene) => { s.distance = distance; drawRuler(s); drawAreas(s, colourRef.current); };
    late.current.run('distance', apply);
    return undefined;
  }, [distance]);

  // Auras (issue 1150): drawn where each token stands in this viewer's store, so they move with it.
  useEffect(() => {
    const apply = (s: Scene) => drawAuras(s, Object.values(store.state.objects) as never);
    late.current.run('auras', apply);
    return undefined;
  }, [store.state.objects, store.state.grid, distance]);

  // Focus highlights (slice 13): everyone's, in identity colours read from the members list.
  useEffect(() => {
    const colours: Record<string, number> = {};
    for (const m of table.members) if (m.colour) colours[m.id] = parseInt(identityColour(m.colour).slice(1), 16);
    const apply = (s: Scene) => { s.focuses = store.state.focuses ?? {}; s.areas = Object.values(store.state.areas ?? {}); s.focusColours = colours; drawFocuses(s, colourRef.current); drawAreas(s, colourRef.current); };
    late.current.run('focuses', apply);
    return undefined;
  }, [store.state.focuses, store.state.areas, table.members, look.palette, look.theme]); // and the person's look (issue 1445)

  // Drawings on the map (issue 1032): a node per drawing, rebuilt only when that drawing changes.
  useEffect(() => {
    const apply = (s: Scene) => { s.ownHex = ownHex; syncDrawings(s, store.state.drawings ?? []); };
    late.current.run('drawings', apply);
    return undefined;
  }, [store.state.drawings, ownHex]);

  // The things the projection gave this person (a hidden one is not in the store to draw), the scene's snapping, and the turn order's reading.
  useEffect(() => {
    const objects = floorThings(store.state, meId, isDm);
    selection.keepOnly(new Set(Object.keys(objects)));
    const raw = store.state.encounter as Encounter | PlayerEncounter | undefined;
    const reading: PlayerEncounter | null = !raw ? null : isDm ? dmEncounterView(raw as Encounter) : (raw as PlayerEncounter);
    const acting = reading?.active ? reading.combatants.find((c) => c.id === reading.active) : null;
    const turnTokens = new Set((acting?.tokens ?? []).filter((id) => objects[id]));
    const placing = isDm && store.state.placing === true;
    // The side outlines (qa-runs#1157): every token's on the DM's own board, a player's own (and view-as's) otherwise.
    const sides = sideOutlines(settings.outlines !== false, isDm && !viewingAs);
    const toggleRings = new Map<string, number>();
    for (const id of Object.keys((store.state as { toggles?: Record<string, unknown> }).toggles ?? {})) { const r = objects[id] ? toggleRing(store.state, id) : null; if (r) toggleRings.set(id, Number.parseInt(r.colour.slice(1), 16)); }
    const apply = (s: Scene) => { s.floors = store.state.scene.floors; s.snap = store.state.scene.snap; s.overlays = store.state.scene.overlays; s.encounter = reading; s.turnTokens = turnTokens; s.toggleRings = toggleRings; s.placing = placing; s.sides = sides; drawThings(s, objects, isDm, meId); refreshMoveRef.current(); };
    late.current.run('things', apply);
    return undefined;
  }, [store.state.objects, store.state.scene, store.state.encounter, (store.state as { toggles?: unknown }).toggles, store.state.placing, colour, isDm, meId, look.palette, look.theme, look.text, settings.outlines, viewingAs, pickedFloor]);

  // A move that arrived: the token travels its route, for everyone, the mover included.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !travel) return;
    const t: Travel = travel;
    const started = performance.now();
    for (const m of t.moves) scene.travels.set(m.id, { route: m.route, started, ms: travelMs(m.route, scene.objectsGrid.x.basePitch) });
  }, [travel]);

  // A compendium result dropped here (issue 1045): the DM's alone, not while looking through a player's eyes.
  return <div ref={host} tabIndex={0} role="application" aria-roledescription="board" aria-label="The board. Its tokens are listed by View, Tokens; arrow keys move a selected token of yours." className="h-full w-full touch-none select-none outline-none focus:ring-1 focus:ring-amber-500/40" data-testid="board-host" {...boardDropProps(role === 'dm' && !viewingAs)} />;
}
